import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import { Pagination, Tooltip, Menu, MenuItem } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { AnalyzersAPI, AceBulkFilter } from 'api/AnalyzersAPI';
import moment from 'moment';
import { Modal } from '@mui/material';
import DownloadModal from 'components/Monitor/Modals/DownloadModal';
import UnifiedFilterModal from 'components/Analyzers/UnifiedFilterModal';
import BulkActionsModal, {
  BulkAction,
} from 'components/Analyzers/BulkActionsModal';
import AceTableExportModal from 'components/Analyzers/AceTableExportModal';
import { saveAs } from 'file-saver';

// MUI Icons
import IosShareIcon from '@mui/icons-material/IosShare';
import ReplayIcon from '@mui/icons-material/Replay';
import DescriptionIcon from '@mui/icons-material/Description';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { enqueueSnackbar } from 'notistack';
import DeleteConfirmationModal from 'components/Analyzers/DeleteConfirmationModal';
import { GlobalContext } from '../../providers/GlobalProvider';
import ToolBreadcrumb from 'components/Common/ToolBreadcrumb';
import FilterListIcon from '@mui/icons-material/FilterList';
import LayersIcon from '@mui/icons-material/Layers';
import { CircularProgress } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
// BITE Findings Report (PDF). Sits next to "Generate IEEPA Report" in the
// Uploaded Data toolbar; clicking it presigns the PDF that the processor
// already built at finalize time and triggers a browser download.
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ColumnPicker from 'components/Analyzers/columns/ColumnPicker';
import { useSavedColumns } from 'components/Analyzers/columns/useSavedColumns';
import {
  ACE_COLUMNS,
  ACE_MAX_VISIBLE_COLUMNS,
  ACE_TABLE_KEY,
  extractItDate,
} from 'components/Analyzers/columns/aceColumns';

// Map of prefix → human label, matched to the labels used in extractLineTariffs
// so the "Type" column reads identically across Applied vs Expected tables.
const EXPECTED_PREFIX_LABEL: Record<string, string> = {
  ch98_hts2: 'Chapter 98',
  sec_301: 'Section 301',
  sec_301_flip: 'Section 301 Flip',
  sec_232_auto: 'Sec 232 Auto',
  sec_232_mhdv: 'Sec 232 MHDV',
  sec_232_steel: 'Sec 232 Steel',
  sec_232_alum: 'Sec 232 Aluminum',
  sec_232_copper: 'Sec 232 Copper',
  sec_232_consol_metal: 'Sec 232 Consololidated Metal',
  sec_232_sw_timber: 'Sec 232 Softwood Timber',
  sec_232_semiconductor: 'Sec 232 Semiconductor',
  sec_232_pharma: 'Sec 232 Pharma',
  sec_232_uas: 'Sec 232 UAS',
  ieepa_co_specific: 'IEEPA Country Specific',
  ieepa_reciprocal: 'IEEPA Reciprocal',
  sec_122: 'Section 122',
  sec_338: 'Section 338',
  sec_201_qsp: 'Sec 201 QSP',
  civil_air: 'Civil Aircraft',
  other_ch99: 'Other Chapter 99',
};

// Display order for BITE Expected Tariffs. Mirrors the engine's
// reorderTariffRows bucket logic in
// automations/src/utils/tariffCalculation.util.ts (bucketOf, ~L840) so the
// drawer renders codes in the same order the Single Tariff Tool does:
//
//   [1, 0]  Chapter 98
//   [2, 0]  Section 301
//   [2, 0.5] Section 301 Flip (sec_301_flip bucket — the 9903.05/06 codes),
//           keyed by prefix so it renders at the tail of the 301 group
//   [2, 1]  Section 122
//   [2, 2]  IEEPA Country Specific (Fentanyl / Russian Oil / etc.)
//   [2, 4]  IEEPA Reciprocal
//   [2, 6]  Section 232 (any sub-flavor: Auto, MHDV, Steel, Alum, Copper,
//           Consolidated Metal, Softwood Timber, Semiconductor, Pharma)
//   [3, 1]  Civil Aircraft and other Ch.99 (residual)
//
// Items inside the same bucket sort by HTS digits ascending so the
// rendering is deterministic across page loads and re-runs.
const EXPECTED_PREFIX_BUCKET: Record<string, [number, number]> = {
  ch98_hts2: [1, 0],
  sec_301: [2, 0],
  sec_301_flip: [2, 0.5],
  // Section 338 sits between the 301 group and Section 122, matching
  // bucketOf() in the engine.
  sec_338: [2, 0.75],
  sec_122: [2, 1],
  ieepa_co_specific: [2, 2],
  ieepa_reciprocal: [2, 4],
  sec_232_auto: [2, 6],
  sec_232_mhdv: [2, 6],
  sec_232_steel: [2, 6],
  sec_232_alum: [2, 6],
  sec_232_copper: [2, 6],
  sec_232_consol_metal: [2, 6],
  sec_232_sw_timber: [2, 6],
  sec_232_semiconductor: [2, 6],
  sec_232_pharma: [2, 6],
  sec_232_uas: [2, 6],
  // Section 201 duties share the 232 slot in the engine's bucketOf().
  sec_201_qsp: [2, 6],
  civil_air: [3, 1],
  other_ch99: [3, 1],
};

// Parse the row's expected_tariffs JSONB column into table rows. The processor
// stores [{ prefix, hts, type, rate, duty }] for each Ch.99 code BITE expected
// on this line. The shape of the table matches Applied Tariffs (Type, HTS,
// Description, Duty) — `type` from the rule's tariff_list_name is used as the
// Description, and Type comes from the prefix label.
const extractExpectedTariffs = (line: any) => {
  if (!line) return [];
  const raw = line.expected_tariffs;
  if (!raw) return [];
  // The server may return JSONB as an already-parsed array OR as a string,
  // depending on the pg client config — handle both.
  let arr: any[] = [];
  if (Array.isArray(raw)) {
    arr = raw;
  } else if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) arr = parsed;
    } catch {
      return [];
    }
  } else {
    return [];
  }
  const rows = arr.map((t: any) => {
    const prefix = String(t?.prefix || '');
    // sec_301_flip carries its own [2, 0.5] entry in EXPECTED_PREFIX_BUCKET, so
    // the flip codes render at the tail of the 301 group with no special-casing.
    const bucket = EXPECTED_PREFIX_BUCKET[prefix] || [3, 1];
    return {
      type: EXPECTED_PREFIX_LABEL[prefix] || 'Other Chapter 99',
      hts: String(t?.hts || ''),
      desc: String(t?.type || '--'),
      duty: Number(t?.duty) || 0,
      // expectedOrdinal is stamped by the processor (see processCalculations
      // — expectedOrdinalByHts). Falls back to 0 only on legacy runs that
      // pre-date the ordering work.
      expectedOrdinal: Number(t?.expectedOrdinal) || 0,
      _bucket: bucket,
    };
  });
  rows.sort((a, b) => {
    const [ap, as] = a._bucket;
    const [bp, bs] = b._bucket;
    if (ap !== bp) return ap - bp;
    if (as !== bs) return as - bs;
    return a.hts.localeCompare(b.hts);
  });
  return rows.map((r) => ({
    type: r.type,
    hts: r.hts,
    desc: r.desc,
    duty: r.duty,
    expectedOrdinal: r.expectedOrdinal,
  }));
};

// Read the customer's applied tariffs straight out of tariff_hits — the
// JSONB column captures every slot (primary + extras) along with the
// per-hit ordinal we now resolve in flattenGroup. Sorted by ordinal
// ascending so the drawer shows the row in the same order the customer
// filed it, which is what makes the "Tariff Ordering" flag legible.
const extractLineTariffs = (line: any) => {
  if (!line) return [];
  const raw = line.tariff_hits;
  let hits: any[] = [];
  if (Array.isArray(raw)) {
    hits = raw;
  } else if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) hits = parsed;
    } catch {
      hits = [];
    }
  }

  const rows = hits.map((h: any) => ({
    type: EXPECTED_PREFIX_LABEL[String(h?.prefix || '')] || 'Other Chapter 99',
    hts: String(h?.hts || ''),
    desc: String(h?.desc || '--'),
    duty: Number(h?.duty) || 0,
    ordinal: Number(h?.ordinal) || 0,
  }));

  rows.sort((a, b) => {
    if (a.ordinal !== b.ordinal) return a.ordinal - b.ordinal;
    return a.hts.localeCompare(b.hts);
  });
  return rows;
};

// ---------------------------------------------------------------------------
// Flag chip color mapping
// ---------------------------------------------------------------------------
// Data Error and Processing Error are different in kind from the BITE/Ch.99
// findings: they indicate the row was UNREADABLE or UNPROCESSABLE in the
// uploaded file — not a tariff discrepancy we calculated. Reviewers need to
// see those at a glance without reading the chip text. The remaining flag
// types are normal findings and stay on the existing blue chip.
//
// Returns the CSS class to append after `screening-badge lens-chip`.
// Severity-driven: the processor stamps each flag with 'Red' / 'Yellow' /
// (anything else / missing) → matches chip-red / chip-yellow / chip-blue,
// all three of which exist in theme.css. This keeps the outline shape
// identical across types and lets the chip's color carry the severity
// at a glance — red for critical accuracy issues, yellow for follow-up
// items, blue for informational chips.
//
// Type-level overrides handle flags whose stored severity doesn't reflect
// the product-level classification (e.g. "Needs Tariff Tool Review" is
// emitted with severity 'Yellow' for historical reasons but is purely
// informational — the engine couldn't make a determination, not that
// anything is wrong). This guarantees the right color even for runs
// produced before the processor's severity for that flag was revisited.
const INFO_FLAG_TYPES = new Set<string>(['Needs Tariff Tool Review']);
const flagChipClass = (flag: any): string => {
  if (flag?.type && INFO_FLAG_TYPES.has(String(flag.type))) return 'chip-blue';
  switch (String(flag?.severity || '').toLowerCase()) {
    case 'red':
      return 'chip-red';
    case 'yellow':
      return 'chip-yellow';
    default:
      return 'chip-blue';
  }
};

// Stable signature for a flag — mirrors the server's flagSignature() in
// AnalyzersService. Used to match a row's flag against the cleared_flags
// list and to identify which finding the per-flag clear/restore buttons
// should target.
const flagSignature = (f: any): string =>
  `${String(f?.type ?? '')}|${String(f?.message ?? '')}`;

// ---------------------------------------------------------------------------
// Section 232 metal-content split
// ---------------------------------------------------------------------------
// A Section 232 derivative duty is owed on the metal content portion of a line,
// not the whole line. The processor derives that share from the filed quantities
// (see AnalyzerUtils/metalContentShare) and persists the arithmetic in
// metal_content_derivation. The drawer replays it so a duty finding on the line
// is auditable without re-deriving it by hand.
//
// JSONB can arrive already parsed OR as a JSON string depending on the pg driver
// config — the same dual handling as parseClearedFlags below.
const parseMetalDerivation = (raw: any): any | null => {
  if (!raw) return null;
  let parsed: any = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }
  if (!parsed || typeof parsed !== 'object') return null;
  return Array.isArray(parsed.detail) ? parsed : null;
};

const formatUsd = (n: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

// Shares can legitimately be sub-1% (a small steel component in a mostly
// non-metal article), so don't round those away to "0%". Anything above 100% is
// shown AS 100% because that is the share the duty was actually priced on — the
// raw figures are spelled out underneath instead of putting an impossible
// percentage on the chip.
const formatContentPct = (pct: number | null): string => {
  if (pct === null || !Number.isFinite(pct)) return '—';
  if (pct >= 100) return '100%';
  if (pct > 0 && pct < 0.1) return '<0.1%';
  return `${pct.toFixed(pct < 10 ? 2 : 1)}%`;
};

// Trim trailing zeros so a NUMERIC(15,5) round-trip doesn't render "3.39000 KG".
const formatQty = (n: number, unit: string | null): string => {
  const s = Number(Number(n).toFixed(5)).toString();
  return unit ? `${s} ${unit}` : s;
};

/** How the share was measured, in the reviewer's terms. */
const describeContentBasis = (d: any): string => {
  if (d?.basis === 'undeclared') {
    return 'No Chapter 99 line was filed for this metal, so the entry declares no content.';
  }
  if (d?.numerator === null || d?.denominator === null) return '';
  if (d?.basis === 'declared value') {
    return `Declared content value ${formatUsd(d.numerator)} of ${formatUsd(
      d.denominator,
    )} line value`;
  }
  if (d?.basis === 'declared duty') {
    // No content quantity was filed, but the duty paid pins the content value:
    // duty / rate. Matching the full line value means wholly metal.
    return `Duty filed implies ${formatUsd(d.numerator)} of content on a ${formatUsd(
      d.denominator,
    )} line — declared as wholly metal`;
  }
  return `Filed content ${formatQty(d.numerator, d.unit)} of ${formatQty(
    d.denominator,
    d.unit,
  )} on the base line`;
};

// Explains an over-100% share in place, so it reads as understood rather than
// as a silent clamp. CBP never specified a basis for the Chapter 99 content
// weight: the Chapter 1-97 line carries the classified article's net weight
// while the content figure comes off the commercial invoice, so a modest
// overshoot is a gross-vs-net difference, not an error — and the duty is the
// same either way, because CBP assesses on the entire entered value once
// content reaches the entered value.
const describeOverDeclared = (d: any): string => {
  const isValue = d?.basis === 'declared value';
  const raw =
    d?.pct !== null && Number.isFinite(d?.pct) ? ` (${d.pct.toFixed(1)}% as filed)` : '';
  return isValue
    ? `Declared content value exceeds the line total${raw} — treated as wholly metal, duty priced on the full line value.`
    : `Declared content weight exceeds the line's reported weight${raw} — the two are measured on different bases, so this line is treated as wholly metal.`;
};

// Short, cause-specific hints. The full sentence lives on the flag message the
// processor wrote (describeMetalContentGaps); this is the at-a-glance version
// shown next to the split itself.
const METAL_GAP_HINT = {
  no_content_quantity:
    'The Chapter 99 line reports no Line Tariff Quantity or UOM, so the content share cannot be measured.',
  no_common_unit:
    'The Chapter 99 line and the base line report quantities in different units of measure, so they cannot be compared.',
  no_base_quantity:
    'The base line reports no tariff quantity, so there is nothing to measure the content against.',
  unmapped_split_field:
    'This split is not a metal content measure, so the filed quantities cannot determine it.',
};

// Parse the cleared_flags payload the server returns alongside each line.
// JSONB can arrive as an already-parsed array OR as a JSON string depending
// on the pg driver config — handle both. Returns a Map keyed by signature
// so the render layer doesn't .find() repeatedly.
type ClearedFlagEntry = {
  signature: string;
  clearedByUserId: string | null;
  clearedByName: string;
  clearedAt: string;
};
const parseClearedFlags = (raw: any): Map<string, ClearedFlagEntry> => {
  const out = new Map<string, ClearedFlagEntry>();
  let arr: any[] = [];
  if (Array.isArray(raw)) arr = raw;
  else if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) arr = parsed;
    } catch {
      return out;
    }
  }
  for (const e of arr) {
    const sig = String(e?.signature ?? '');
    if (!sig) continue;
    out.set(sig, {
      signature: sig,
      clearedByUserId: e?.clearedByUserId ?? null,
      clearedByName: String(e?.clearedByName ?? 'Unknown User'),
      clearedAt: String(e?.clearedAt ?? ''),
    });
  }
  return out;
};

// On-demand CSV export of the table view. The export runs entirely in the
// browser: it fetches every row matching the current tab/filters in a single
// page, builds the CSV string, and hands it to the browser. To keep that fetch
// + in-memory string from getting out of hand on very large runs, we cap the
// export and ask the user to narrow with the tabs/filters when they're over.
const CSV_EXPORT_ROW_LIMIT = 10000;

// RFC-4180-style cell escaping. We always quote so embedded commas, quotes,
// arrows/em-dashes, and newlines in finding text can never shift columns.
const csvCell = (val: unknown): string => {
  const s = val === null || val === undefined ? '' : String(val);
  return `"${s.replace(/"/g, '""')}"`;
};

// Normalize a row's `flags` (JSONB that may arrive as an array or a JSON
// string) into an array of objects, mirroring the table's own parsing.
const parseFlagsForExport = (raw: any): any[] => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

// The "What we detected" text for a single flag — "<type>: <message>",
// matching the drawer's WHAT WE DETECTED list. Each of these becomes one
// "Flag N" column in the export.
const flagToDetectedText = (f: any): string => {
  const type = f?.type ? String(f.type) : '';
  const message = f?.message ? String(f.message) : '';
  if (type && message) return `${type}: ${message}`;
  return type || message;
};

// USD currency string identical to the table cells. Returns '' (blank cell)
// for empty values; pass requireDefined=true for the DUTY column so a real 0
// renders "$0.00" but null/undefined stays blank (matches the table logic).
const csvCurrency = (v: any, requireDefined = false): string => {
  const empty = requireDefined ? v === null || v === undefined : !v;
  if (empty) return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(n);
};

const AceRunDetailsPage: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const [activeTab, setActiveTab] = useState('All Lines');
  const [selectedLine, setSelectedLine] = useState<any | null>(null);
  // Drawer visibility is tracked separately from selectedLine so the table
  // row highlight survives drawer close — users can scan adjacent rows
  // visually without losing their place. selectedLine stays populated;
  // only isDrawerOpen flips when the drawer is dismissed.
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // Cross-page prev/next nav. When the user hits prev/next at a page
  // boundary we change the page and remember whether to open the FIRST
  // (after "Next") or LAST (after "Previous") row of the freshly-fetched
  // page. The fetch-lines effect picks up this intent and selects the row
  // once the data arrives, then clears the marker.
  const [pendingDrawerNav, setPendingDrawerNav] = useState<
    'first' | 'last' | null
  >(null);

  // --- Server-Side Pagination & Data State ---
  const [runDetails, setRunDetails] = useState<any>(null);
  const [lines, setLines] = useState<any[]>([]);
  const [unmatchedLines, setUnmatchedLines] = useState<any[]>([]);
  const [totalLines, setTotalLines] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Server-side sort. `key` is one of the whitelisted columns in the
  // backend's RUN_LINES_SORT_COLUMNS map (and the matching unmatched-row
  // map); an empty key falls back to the legacy default ordering. Clicking
  // a header toggles between asc and desc and re-fetches page 1.
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  }>({ key: '', direction: 'desc' });

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key: key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const renderSortIndicator = (key: string) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? (
      <ArrowUpwardIcon sx={{ fontSize: 14 }} />
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: 14 }} />
    );
  };

  const { visible: visibleColumnKeys, setVisible: setVisibleColumnKeys } =
    useSavedColumns(ACE_TABLE_KEY, ACE_COLUMNS, ACE_MAX_VISIBLE_COLUMNS);
  const shownColumns = ACE_COLUMNS.filter((c) =>
    visibleColumnKeys.includes(c.key),
  );
  // The chosen columns plus the fixed status marker, Entry #, Line and Flags.
  const tableColSpan = shownColumns.length + 4;

  const handleColumnsChange = (next: typeof visibleColumnKeys) => {
    const sortedColumnHidden = ACE_COLUMNS.some(
      (c) => c.sortKey === sortConfig.key && !next.includes(c.key),
    );
    if (sortedColumnHidden) {
      setSortConfig({ key: '', direction: 'desc' });
      setCurrentPage(1);
    }
    setVisibleColumnKeys(next);
  };

  // --- Download Modal States (S3 exports: original / flattened / results / unmatched) ---
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadOpts, setDownloadOpts] = useState<string>('original');
  const [downloadFileName, setDownloadFileName] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [isReRunning, setIsReRunning] = useState(false);
  // Whether the NEXT re-run should drop every IEEPA rule from the engine
  // (Tariff Tool parity). Seeded from the run's stored setting below so the
  // control shows how the current results were produced; changing it and
  // hitting Re-Run switches the run over and persists the new value.
  const [excludeIeepa, setExcludeIeepa] = useState(false);
  // Re-Run now opens a mode-picker modal (Re-Run vs Re-Run Excluding IEEPA)
  // instead of a standalone toggle next to the button.
  const [showReRunModal, setShowReRunModal] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showBulkActionsModal, setShowBulkActionsModal] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  // On-demand CSV export of the current table view.
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [activeTariffFilters, setActiveTariffFilters] = useState<string[]>([]);
  const [activeFindingFilters, setActiveFindingFilters] = useState<string[]>(
    [],
  );
  const [activeCountryFilters, setActiveCountryFilters] = useState<string[]>(
    [],
  );
  // Filter-option catalog actually present on this run (which tariff
  // prefixes / flags / COOs appear at least once). Fetched once after
  // the run loads and refreshed alongside the lines so a re-run reflects
  // its new shape. Modal hides options that aren't here.
  const [runFilterOptions, setRunFilterOptions] = useState<{
    tariffPrefixes: string[];
    flagTypes: string[];
    countries: string[];
  }>({ tariffPrefixes: [], flagTypes: [], countries: [] });
  const [isGeneratingIeepa, setIsGeneratingIeepa] = useState(false);
  const [isClearingFindings, setIsClearingFindings] = useState(false);

  // --- IEEPA Download Modal States ---
  // Kept separate from the S3-download modal state above so the two modals
  // can coexist without stepping on each other's selections.
  //   'cape_phase_1'       = rules-applied CAPE Phase I Eligibility Report
  //                          (backend runs the full 11-rule IEEPA
  //                          eligibility WHERE clause). The original
  //                          behavior.
  //   'all_ieepa'          = same CSV schema, but the backend skips every
  //                          eligibility rule except "row must have at
  //                          least one IEEPA tariff hit." Useful when the
  //                          eligibility rules are dropping rows the user
  //                          expected to see.
  //   'phase_1_exclusions' = complement of the eligibility report. Includes
  //                          every row with an IEEPA tariff hit that FAILS
  //                          at least one Phase I rule (so it would be
  //                          excluded from the Phase I eligibility export).
  //                          Useful for auditing the filter.
  const [showIeepaModal, setShowIeepaModal] = useState(false);
  const [ieepaDownloadOpts, setIeepaDownloadOpts] =
    useState<string>('cape_phase_1');
  const [ieepaFileName, setIeepaFileName] = useState('');

  // Run-status edit popover. Only opens when the run is in one of the two
  // settled states ('Completed' / 'Needs Review'); otherwise the pencil
  // doesn't render. updatingStatus blocks repeated clicks while the API
  // call is in flight.
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [updatingStatus, setUpdatingStatus] = useState(false);
  // "Create Report" consolidates IEEPA/BITE/Build Report under one menu.
  const [reportMenuAnchor, setReportMenuAnchor] = useState<null | HTMLElement>(
    null,
  );

  const handleStatusChange = async (
    nextStatus: 'Completed' | 'Needs Review',
  ) => {
    if (!runId) return;
    setStatusMenuAnchor(null);
    if (runDetails?.status === nextStatus) return;
    setUpdatingStatus(true);
    try {
      const resp = await AnalyzersAPI.updateAceRunStatus(runId, nextStatus);
      if (resp?.run?.status) {
        setRunDetails((prev: any) => ({ ...prev, status: resp.run!.status }));
        enqueueSnackbar(`Status updated to ${resp.run.status}.`, {
          variant: 'success',
        });
      }
    } catch (err) {
      console.error('Failed to update run status:', err);
      enqueueSnackbar('Failed to update run status.', { variant: 'error' });
    } finally {
      setUpdatingStatus(false);
    }
  };

  // BITE Findings Report (PDF) — downloaded as a single click. The PDF was
  // built and uploaded by the automations processor at finalize time, so
  // here we just presign and trigger the browser download. We track an
  // in-flight flag purely to disable the button + render a spinner while
  // the presign + download is happening.
  const [isDownloadingBiteReport, setIsDownloadingBiteReport] = useState(false);

  const history = useHistory();
  const { cur_user } = useContext(GlobalContext);

  const [, setUserHasMonitor] = useState(true);

  const loadingMessages = [
    'Flattening entry lines...',
    'Cross-referencing Chapter 99 provisions...',
    'Calculating BITE tariff expectations...',
    'Evaluating AD/CVD and PGA flags...',
    'Identifying discrepancies and anomalies...',
  ];
  const [loadingText, setLoadingText] = useState(loadingMessages[0]);

  useEffect(() => {
    const isProcessing =
      runDetails?.status === 'Running' || runDetails?.status === 'Pending';
    if (!isProcessing) return;

    let index = 0;
    const interval = setInterval(() => {
      index = (index + 1) % loadingMessages.length;
      setLoadingText(loadingMessages[index]);
    }, 3000);

    return () => clearInterval(interval);
  }, [runDetails?.status]);

  useEffect(() => {
    checkForMonitor();
  }, []);

  const requiredTiers = ['pro', 'business', 'enterprise'];

  const checkForMonitor = () => {
    if (!requiredTiers.includes(cur_user.license)) {
      setUserHasMonitor(false);
    }
  };

  // Fetch the filter-options catalog once per run. Modal opens fast
  // because the data is already in state; refreshes only when runId
  // changes (or after a re-run, which causes the page to remount).
  useEffect(() => {
    if (!runId) return;
    AnalyzersAPI.getAceRunFilterOptions(runId)
      .then((opts) => setRunFilterOptions(opts))
      .catch((err) => {
        console.error('Failed to fetch filter options:', err);
        // Empty catalog, but SAY SO. An empty catalog renders as a filter
        // modal and a bulk-actions modal with every section hidden, which is
        // visually identical to "this run has nothing to filter by" — so a
        // server-side failure here presented as two silently broken modals and
        // took a round of QA to even identify as an error. The console line
        // alone was not enough; a tester does not have DevTools open.
        setRunFilterOptions({
          tariffPrefixes: [],
          flagTypes: [],
          countries: [],
        });
        enqueueSnackbar(
          'Could not load filter options for this run — filters and bulk actions will be empty.',
          { variant: 'error' },
        );
      });
  }, [runId]);

  useEffect(() => {
    if (!runId) return;
    AnalyzersAPI.getAceRunDetails(
      runId,
      activeTariffFilters.join(','),
      activeFindingFilters.join(','),
      activeCountryFilters.join(','),
    )
      .then((data: any) => setRunDetails(data))
      .catch(console.error);
  }, [runId, activeTariffFilters, activeFindingFilters, activeCountryFilters]);

  // Reset to page 1 whenever the tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // Fetch Paginated Lines & Unmatched Rows
  useEffect(() => {
    if (!runId) return;
    const fetchLines = async () => {
      setLoading(true);
      try {
        if (activeTab === 'Unmatched Rows') {
          const data = await AnalyzersAPI.getAceUnmatchedRows(
            runId,
            currentPage,
            pageSize,
            debouncedSearch || undefined,
            sortConfig.key || undefined,
            sortConfig.key ? sortConfig.direction : undefined,
          );
          setUnmatchedLines(data.rows);
          setTotalLines(data.total);
        } else {
          const data = await AnalyzersAPI.getAceRunLines(
            runId,
            currentPage,
            pageSize,
            activeTab,
            debouncedSearch,
            activeTariffFilters.join(','),
            activeFindingFilters.join(','),
            activeCountryFilters.join(','),
            sortConfig.key || undefined,
            sortConfig.key ? sortConfig.direction : undefined,
          );
          setLines(data.lines);
          setTotalLines(data.total);
          // Cross-page drawer navigation: the prev/next arrows trigger a
          // page change AND set pendingDrawerNav. Once the new page lands,
          // open the appropriate boundary row and clear the marker. We
          // route through openLineInDrawer so parsedFlags is computed the
          // same way as a normal row click.
          if (
            pendingDrawerNav &&
            Array.isArray(data.lines) &&
            data.lines.length > 0
          ) {
            const target =
              pendingDrawerNav === 'first'
                ? data.lines[0]
                : data.lines[data.lines.length - 1];
            const flagsArray =
              typeof target.flags === 'string'
                ? JSON.parse(target.flags)
                : target.flags || [];
            setSelectedLine({ ...target, parsedFlags: flagsArray });
            setIsDrawerOpen(true);
            setPendingDrawerNav(null);
          } else if (pendingDrawerNav) {
            // Empty page (e.g. filters changed mid-flight) — clear marker
            // so the buttons re-enable without leaving the drawer stuck.
            setPendingDrawerNav(null);
          }
        }
      } catch (error) {
        console.error('Failed to fetch lines:', error);
      } finally {
        setLoading(false);
        setShowFilterModal(false);
      }
    };
    fetchLines();
  }, [
    runId,
    currentPage,
    pageSize,
    activeTab,
    debouncedSearch,
    activeTariffFilters,
    activeFindingFilters,
    activeCountryFilters,
    sortConfig.key,
    sortConfig.direction,
  ]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    const isProcessing =
      runDetails?.status === 'Running' || runDetails?.status === 'Pending';

    if (isProcessing && runId) {
      intervalId = setInterval(async () => {
        try {
          const updatedDetails = await AnalyzersAPI.getAceRunDetails(
            runId,
            activeTariffFilters.join(','),
            activeFindingFilters.join(','),
            activeCountryFilters.join(','),
          );
          setRunDetails(updatedDetails);

          if (activeTab === 'Unmatched Rows') {
            const data = await AnalyzersAPI.getAceUnmatchedRows(
              runId,
              currentPage,
              pageSize,
              debouncedSearch || undefined,
              sortConfig.key || undefined,
              sortConfig.key ? sortConfig.direction : undefined,
            );
            setUnmatchedLines(data.rows);
            setTotalLines(data.total);
          } else {
            const updatedLines = await AnalyzersAPI.getAceRunLines(
              runId,
              currentPage,
              pageSize,
              activeTab,
              debouncedSearch,
              activeTariffFilters.join(','),
              activeFindingFilters.join(','),
              activeCountryFilters.join(','),
              sortConfig.key || undefined,
              sortConfig.key ? sortConfig.direction : undefined,
            );
            setLines(updatedLines.lines);
            setTotalLines(updatedLines.total);
          }
        } catch (error) {
          console.error('Failed to poll run details:', error);
        }
      }, 3000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [
    runDetails?.status,
    runId,
    currentPage,
    pageSize,
    activeTab,
    debouncedSearch,
  ]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const executeDeleteRun = async () => {
    if (!runId) return;
    setIsDeleting(true);
    try {
      await AnalyzersAPI.deleteAceRun(runId);
      setDeleteModalOpen(false);
      history.push('/ace-analyzers');
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          'Failed to delete the run. Please try again.',
        { variant: 'error' },
      );
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    setExcludeIeepa(runDetails?.exclude_ieepa === true);
  }, [runDetails?.exclude_ieepa]);

  // excludeIeepaOverride lets the Re-Run modal's two options each fire
  // immediately on click (no separate confirm step) instead of racing the
  // excludeIeepa state update; defaults to that state for any other caller.
  const handleReRun = async (excludeIeepaOverride?: boolean) => {
    if (!runDetails || !runDetails.original_s3_file_key || !runId) {
      enqueueSnackbar('Missing original file information to re-run.', {
        variant: 'error',
      });
      return;
    }

    const shouldExcludeIeepa = excludeIeepaOverride ?? excludeIeepa;
    setIsReRunning(true);
    try {
      const firstName = cur_user?.basics?.firstName?.trim?.() || '';
      const lastName = cur_user?.basics?.lastName?.trim?.() || '';
      const initiatedByName =
        `${firstName} ${lastName}`.trim() ||
        cur_user?.basics?.username ||
        cur_user?.basics?.email ||
        '';

      await AnalyzersAPI.reRunAceAnalysis(
        runId,
        runDetails.original_s3_file_key,
        runDetails.group_id,
        shouldExcludeIeepa,
        {
          id: cur_user?.basics?.id || null,
          name: initiatedByName,
          email: cur_user?.basics?.email || null,
        },
      );

      enqueueSnackbar('Re-Run successfully started!', { variant: 'success' });

      setRunDetails((prev: any) => ({
        ...prev,
        status: 'Pending',
        findings_count: 0,
        exclude_ieepa: shouldExcludeIeepa,
      }));
      setLines([]);
      setUnmatchedLines([]);
      setTotalLines(0);
      setCurrentPage(1);
      setActiveTab('All Lines');
    } catch (error) {
      console.error('Failed to start re-run:', error);
      enqueueSnackbar('Failed to trigger re-run. Please try again.', {
        variant: 'error',
      });
    } finally {
      setIsReRunning(false);
    }
  };

  // Non-destructive clear. Optionally targets a specific finding by its
  // signature (per-row X button); omitting signatures clears every
  // currently-active finding on the row ("Clear All Findings"). The
  // server returns the merged cleared_flags array so we update local
  // state without a re-fetch.
  const handleClearFindings = async (signatures?: string[]) => {
    if (!selectedLine || !runId) return;

    setIsClearingFindings(true);
    try {
      const resp = await AnalyzersAPI.clearAceLineFindingsBySignature(
        runId,
        selectedLine.id,
        signatures,
      );
      const nextCleared = resp.cleared_flags || [];

      enqueueSnackbar(
        signatures && signatures.length === 1
          ? 'Finding cleared.'
          : 'Findings cleared.',
        { variant: 'success' },
      );

      // The row's flags column is untouched — we just propagate the new
      // cleared_flags array so the drawer renders the visual cleared state.
      setSelectedLine((prev: any) => ({ ...prev, cleared_flags: nextCleared }));
      setLines((prevLines) =>
        prevLines.map((line) =>
          line.id === selectedLine.id
            ? { ...line, cleared_flags: nextCleared }
            : line,
        ),
      );

      AnalyzersAPI.getAceRunDetails(
        runId,
        activeTariffFilters.join(','),
        activeFindingFilters.join(','),
        activeCountryFilters.join(','),
      )
        .then((data: any) => setRunDetails(data))
        .catch(console.error);
    } catch (error) {
      console.error('Failed to clear findings:', error);
      enqueueSnackbar('Failed to clear findings.', { variant: 'error' });
    } finally {
      setIsClearingFindings(false);
    }
  };

  // Restore previously-cleared findings. Pass a single signature to
  // restore one (per-row Restore button) or omit signatures to restore
  // every dismissed finding on the row.
  const handleRestoreFindings = async (signatures?: string[]) => {
    if (!selectedLine || !runId) return;

    setIsClearingFindings(true);
    try {
      const resp = await AnalyzersAPI.restoreAceLineFindingsBySignature(
        runId,
        selectedLine.id,
        signatures,
      );
      const nextCleared = resp.cleared_flags || [];

      enqueueSnackbar(
        signatures && signatures.length === 1
          ? 'Finding restored.'
          : 'Findings restored.',
        { variant: 'success' },
      );

      setSelectedLine((prev: any) => ({ ...prev, cleared_flags: nextCleared }));
      setLines((prevLines) =>
        prevLines.map((line) =>
          line.id === selectedLine.id
            ? { ...line, cleared_flags: nextCleared }
            : line,
        ),
      );

      AnalyzersAPI.getAceRunDetails(
        runId,
        activeTariffFilters.join(','),
        activeFindingFilters.join(','),
        activeCountryFilters.join(','),
      )
        .then((data: any) => setRunDetails(data))
        .catch(console.error);
    } catch (error) {
      console.error('Failed to restore findings:', error);
      enqueueSnackbar('Failed to restore findings.', { variant: 'error' });
    } finally {
      setIsClearingFindings(false);
    }
  };

  // Accept the exact string directly from the modal input, or fall back to
  // state if an event is passed
  const handleExecuteDownload = async (customNameFromModal?: any) => {
    if (!runId || !downloadOpts) return;
    setIsDownloading(true);
    try {
      const apiDocType =
        downloadOpts === 'flattened' ? 'results_clean' : downloadOpts;

      let defaultName = 'ACE_Export';
      if (downloadOpts === 'results') defaultName = 'Analyzed Results';
      else if (downloadOpts === 'flattened') defaultName = 'Flattened Data';
      else if (downloadOpts === 'unmatched') defaultName = 'Unmatched Rows';
      else if (downloadOpts === 'original') defaultName = 'Original Upload';

      const nameToUse =
        typeof customNameFromModal === 'string'
          ? customNameFromModal
          : downloadFileName;
      let finalName = nameToUse?.trim() || defaultName;

      if (finalName.toLowerCase().endsWith('.csv')) {
        finalName = finalName.substring(0, finalName.length - 4);
      }
      const customFileName = `${finalName}.csv`;

      const response = await AnalyzersAPI.getS3DownloadUrl(
        runId,
        apiDocType,
        customFileName,
      );

      const link = document.createElement('a');
      link.href = response.url;
      link.setAttribute('download', customFileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);

      setShowDownloadModal(false);
    } catch (error) {
      console.error('Failed to download file:', error);
      enqueueSnackbar('Failed to retrieve download link from S3', {
        variant: 'error',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  // Submit handler for the Bulk Actions modal. Dispatches based on
  // action, refreshes lines + run details on success so the table and
  // counters reflect what just happened. Snackbar reports rowsAffected
  // so the user knows the action did something even when the affected
  // rows weren't visible in the current filtered view.
  // Read-only count for the modal's live preview. Returns 0 on error so
  // the modal can distinguish "no matches" from a transient failure only
  // via the thrown-vs-zero path; here we swallow and return 0 to keep the
  // footer quiet rather than surfacing a toast on every keystroke.
  /**
   * The table's current view, in the shape the lines endpoint already takes.
   *
   * Sent with every bulk request so an action lands on exactly the rows on
   * screen: search an HTS code down to ten rows, clear missing-ch99 findings,
   * and only those ten are touched. It INTERSECTS with the criteria picked in
   * the bulk dialog rather than replacing them.
   *
   * Empty strings are dropped to undefined so an unfiltered table sends no
   * scope at all and keeps acting on the whole run.
   */
  const currentViewScope = useMemo(
    () => ({
      filterTab: activeTab,
      search: searchQuery.trim() || undefined,
      tariffFilters: activeTariffFilters.join(',') || undefined,
      findingFilters: activeFindingFilters.join(',') || undefined,
      countryFilters: activeCountryFilters.join(',') || undefined,
    }),
    [
      activeTab,
      searchQuery,
      activeTariffFilters,
      activeFindingFilters,
      activeCountryFilters,
    ],
  );

  // Applied here rather than inside the modal so the page stays the single
  // owner of its view state and the modal only has to describe its own criteria.
  const withViewScope = (filter: AceBulkFilter): AceBulkFilter => ({
    ...filter,
    viewScope: currentViewScope,
  });

  // The same narrowing in words, for the bulk dialog. Empty when the table is
  // unfiltered, in which case the dialog says nothing about scope because there
  // is none.
  const viewScopeLabel = useMemo(() => {
    const parts: string[] = [];
    if (activeTab && activeTab !== 'All Lines') parts.push(activeTab);
    const q = searchQuery.trim();
    if (q) parts.push(`search "${q}"`);
    const plural = (n: number, word: string) =>
      `${n} ${word}${n === 1 ? '' : 's'}`;
    if (activeTariffFilters.length)
      parts.push(plural(activeTariffFilters.length, 'tariff filter'));
    if (activeFindingFilters.length)
      parts.push(plural(activeFindingFilters.length, 'finding filter'));
    if (activeCountryFilters.length)
      parts.push(plural(activeCountryFilters.length, 'country filter'));
    return parts.join(' · ');
  }, [
    activeTab,
    searchQuery,
    activeTariffFilters,
    activeFindingFilters,
    activeCountryFilters,
  ]);

  const handleBulkPreview = async (filter: AceBulkFilter): Promise<number> => {
    if (!runId) return 0;
    const res = await AnalyzersAPI.bulkPreviewAceCount(
      runId,
      withViewScope(filter),
    );
    return res?.count ?? 0;
  };

  const handleBulkSubmit = async (params: {
    action: BulkAction;
    filter: AceBulkFilter;
    clearScope: 'all' | 'selected';
    commentText?: string;
  }) => {
    if (!runId) return;
    setBulkActionLoading(true);
    try {
      let result: { success: boolean; rowsAffected: number } | undefined;
      if (params.action === 'clear') {
        result = await AnalyzersAPI.bulkClearAceFindings(
          runId,
          withViewScope(params.filter),
          params.clearScope,
        );
      } else if (params.action === 'restore') {
        result = await AnalyzersAPI.bulkRestoreAceFindings(
          runId,
          withViewScope(params.filter),
          params.clearScope,
        );
      } else if (params.action === 'comment') {
        result = await AnalyzersAPI.bulkAddAceLineComment(
          runId,
          params.commentText || '',
          withViewScope(params.filter),
        );
      } else if (params.action === 'review' || params.action === 'unreview') {
        result = await AnalyzersAPI.bulkSetAceReviewed(
          runId,
          withViewScope(params.filter),
          params.action === 'review',
        );
      }

      const affected = result?.rowsAffected ?? 0;
      // Message reads naturally per action — findings verbs for clear/
      // restore/comment, a status phrasing for review/unreview.
      let successMsg: string;
      if (params.action === 'review' || params.action === 'unreview') {
        const verb = params.action === 'review' ? 'marked reviewed' : 'unmarked';
        successMsg = `${affected.toLocaleString()} transaction${
          affected === 1 ? '' : 's'
        } ${verb}.`;
      } else {
        const verb =
          params.action === 'clear'
            ? 'cleared on'
            : params.action === 'restore'
            ? 'restored on'
            : 'commented on';
        successMsg = `Findings ${verb} ${affected.toLocaleString()} row${
          affected === 1 ? '' : 's'
        }.`;
      }
      enqueueSnackbar(
        affected > 0 ? successMsg : 'No matching rows were found for that filter.',
        { variant: affected > 0 ? 'success' : 'info' },
      );

      setShowBulkActionsModal(false);

      // Refresh both the table and the run-level counters. Re-pull the
      // current page in particular so the user sees per-row state update.
      const refreshLines = AnalyzersAPI.getAceRunLines(
        runId,
        currentPage,
        pageSize,
        activeTab,
        debouncedSearch,
        activeTariffFilters.join(','),
        activeFindingFilters.join(','),
        activeCountryFilters.join(','),
      )
        .then((data) => {
          setLines(data.lines);
          setTotalLines(data.total);
        })
        .catch(console.error);

      const refreshDetails = AnalyzersAPI.getAceRunDetails(
        runId,
        activeTariffFilters.join(','),
        activeFindingFilters.join(','),
        activeCountryFilters.join(','),
      )
        .then((data: any) => setRunDetails(data))
        .catch(console.error);

      await Promise.all([refreshLines, refreshDetails]);
    } catch (error) {
      console.error('Bulk action failed:', error);
      enqueueSnackbar('Bulk action failed. Please try again.', {
        variant: 'error',
      });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const totalPages = Math.ceil(totalLines / pageSize);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setCurrentPage(value);
  };

  // Open the next row from the drawer. When the user is already on the
  // last row of the current page AND there are more pages, we bump the
  // page and let the fetch-lines effect open the first row of the new
  // page (handled via pendingDrawerNav, set below; consumed in the same
  // effect at line ~360). Disables itself while a cross-page jump is in
  // flight so a fast double-click can't stack multiple page changes.
  const openLineInDrawer = (line: any) => {
    if (!line) return;
    const flagsArray =
      typeof line.flags === 'string'
        ? JSON.parse(line.flags)
        : line.flags || [];
    setSelectedLine({ ...line, parsedFlags: flagsArray });
    setIsDrawerOpen(true);
  };

  const handleDrawerNext = () => {
    if (!selectedLine || pendingDrawerNav !== null) return;
    const idx = lines.findIndex((l: any) => l.id === selectedLine.id);
    if (idx < 0) return;
    if (idx < lines.length - 1) {
      openLineInDrawer(lines[idx + 1]);
      return;
    }
    if (currentPage < totalPages) {
      setPendingDrawerNav('first');
      setCurrentPage(currentPage + 1);
    }
  };

  const handleDrawerPrev = () => {
    if (!selectedLine || pendingDrawerNav !== null) return;
    const idx = lines.findIndex((l: any) => l.id === selectedLine.id);
    if (idx < 0) return;
    if (idx > 0) {
      openLineInDrawer(lines[idx - 1]);
      return;
    }
    if (currentPage > 1) {
      setPendingDrawerNav('last');
      setCurrentPage(currentPage - 1);
    }
  };

  const handleMarkReviewed = async () => {
    if (!runId || !selectedLine?.id) return;
    try {
      await AnalyzersAPI.markLineAsReviewed(runId, selectedLine.id);
      enqueueSnackbar('Line marked as reviewed.', { variant: 'success' });
      setSelectedLine((prev: any) => ({ ...prev, is_reviewed: true }));
      setLines((prevLines) =>
        prevLines.map((line) =>
          line.id === selectedLine.id ? { ...line, is_reviewed: true } : line,
        ),
      );
      AnalyzersAPI.getAceRunDetails(
        runId,
        activeTariffFilters.join(','),
        activeFindingFilters.join(','),
        activeCountryFilters.join(','),
      )
        .then((data: any) => setRunDetails(data))
        .catch(console.error);
    } catch (err) {
      enqueueSnackbar('Failed to update status.', { variant: 'error' });
    }
  };

  const handleAddNote = async () => {
    if (!runId || !selectedLine?.id || !newNote.trim()) return;
    setIsSubmittingNote(true);
    const noteText = newNote.trim();
    // Server authoritatively sets the note's `user` from the auth context
    // (see AnalyzersController.addLineNote). We compute the display name
    // here purely for the optimistic update — the server response carries
    // the canonical name back so any mismatch resolves on the next fetch.
    const firstName = cur_user?.basics?.firstName?.trim?.() || '';
    const lastName = cur_user?.basics?.lastName?.trim?.() || '';
    const displayName =
      `${firstName} ${lastName}`.trim() ||
      cur_user?.basics?.email ||
      'Unknown User';
    try {
      const resp = await AnalyzersAPI.addLineNote(
        runId,
        selectedLine.id,
        noteText,
        displayName,
        cur_user?.basics?.id || null,
      );

      const noteObj = resp?.note ?? {
        text: noteText,
        user: displayName,
        timestamp: new Date().toISOString(),
      };
      const updatedNotes = [...(selectedLine.notes || []), noteObj];
      setSelectedLine((prev: any) => ({ ...prev, notes: updatedNotes }));
      setLines((prevLines) =>
        prevLines.map((line) =>
          line.id === selectedLine.id ? { ...line, notes: updatedNotes } : line,
        ),
      );
      setNewNote('');
    } catch (err) {
      enqueueSnackbar('Failed to add note.', { variant: 'error' });
    } finally {
      setIsSubmittingNote(false);
    }
  };

  /**
   * IEEPA export handler. Invoked by the IEEPA DownloadModal's Download
   * button with the user's typed file name.
   *
   * Mode mapping (ieepaDownloadOpts -> backend rulesMode):
   *   'cape_phase_1'       -> rulesMode = 'eligible'
   *       Backend runs the full 11-rule eligibility WHERE clause. The
   *       original CAPE Phase I Eligibility Report.
   *       Default file name: "CAPE Phase I Eligibility.csv".
   *       Snackbar label:    "CAPE Phase I Eligibility Report".
   *
   *   'all_ieepa'          -> rulesMode = 'all'
   *       Backend drops every rule except "row has at least one IEEPA
   *       tariff hit," so users can review every row the data carried
   *       without the eligibility filter.
   *       Default file name: "All IEEPA Rows.csv".
   *       Snackbar label:    "All IEEPA Rows".
   *
   *   'phase_1_exclusions' -> rulesMode = 'ineligible'
   *       Backend applies the Rule 1 filter AND wraps Rules 2–11 in
   *       NOT(...). The result is every IEEPA-bearing row that FAILS at
   *       least one Phase I rule — i.e. what the Phase I export is
   *       leaving out. Same CSV schema so Exclusions vs. Eligibility
   *       reports diff cleanly.
   *       Default file name: "IEEPA Phase I Exclusions.csv".
   *       Snackbar label:    "IEEPA Phase I Exclusions".
   *
   *   'cape_phase_2'       -> rulesMode = 'phase_2'
   *       Same hard eligibility gate as 'cape_phase_1', but each surviving
   *       line is also classified Claimable vs Needs-Review and carries
   *       trade-remedy / AD-CVD / refund-validation flags plus ES-003
   *       verification-gap disclosures. Needs-Review lines are included
   *       (flagged), not dropped. CAPE Phase II Claim Eligibility Report.
   *
   *   'phase_2_exclusions' -> rulesMode = 'phase_2_exclusions'
   *       The Phase II complement (Rule 1 AND NOT Rules 2–11) in the
   *       Phase II column layout, with an Exclusion Reasons column.
   *
   * The custom file name flows all the way into the S3 presigned URL's
   * Content-Disposition header AND into the <a download> attribute, so
   * whatever the user types is what hits their disk.
   */
  const handleGenerateIeepaReport = async (customNameFromModal?: any) => {
    if (!runId) return;
    setIsGeneratingIeepa(true);
    try {
      // Resolve the UI tile to the matching backend rulesMode once up-front
      // so the rest of the handler can stay DRY. Phase II tiles map to the
      // claim-centric modes; the Phase I tiles are unchanged. Mirrors the
      // same mapping on the ACE Analyzer list page (AceAnalyzers.tsx).
      const rulesModeByOpt: Record<
        string,
        | 'eligible'
        | 'all'
        | 'ineligible'
        | 'phase_2'
        | 'phase_2_exclusions'
        | 'phase_2_rollup'
      > = {
        cape_phase_1: 'eligible',
        phase_1_exclusions: 'ineligible',
        all_ieepa: 'all',
        cape_phase_2: 'phase_2',
        phase_2_exclusions: 'phase_2_exclusions',
        cape_phase_2_rollup: 'phase_2_rollup',
      };
      const rulesMode = rulesModeByOpt[ieepaDownloadOpts] ?? 'eligible';

      const reportMeta: Record<typeof rulesMode, { name: string; label: string }> = {
        eligible: {
          name: 'CAPE Phase I Eligibility',
          label: 'CAPE Phase I Eligibility Report',
        },
        ineligible: {
          name: 'IEEPA Phase I Exclusions',
          label: 'IEEPA Phase I Exclusions',
        },
        all: { name: 'All IEEPA Rows', label: 'All IEEPA Rows' },
        phase_2: {
          name: 'CAPE Phase II Claim Eligibility',
          label: 'CAPE Phase II Claim Eligibility Report',
        },
        phase_2_exclusions: {
          name: 'CAPE Phase II Exclusions',
          label: 'CAPE Phase II Exclusions',
        },
        phase_2_rollup: {
          name: 'CAPE Phase II Entry Rollup',
          label: 'CAPE Phase II Entry Rollup',
        },
      };
      const defaultName = reportMeta[rulesMode].name;
      const reportLabel = reportMeta[rulesMode].label;

      const nameToUse =
        typeof customNameFromModal === 'string'
          ? customNameFromModal
          : ieepaFileName;

      let finalName = nameToUse?.trim() || defaultName;
      if (finalName.toLowerCase().endsWith('.csv')) {
        finalName = finalName.substring(0, finalName.length - 4);
      }
      const customFileName = `${finalName}.csv`;

      // API now takes an options bag so we can pick the rules mode and
      // pipe through a custom attachment file name. Older callers that
      // passed only runId still work because the options object is
      // optional on the backend side.
      const { url, totalOutputRows, fileName, message } =
        await AnalyzersAPI.generateIeepaReport(runId, {
          rulesMode: rulesMode,
          customFileName: customFileName,
        });

      // Backend surfaces the "no rows" case via either a zero count or a
      // known message string. Either way we show a tailored info
      // snackbar instead of forcing an empty CSV download.
      if (
        totalOutputRows === 0 ||
        message === 'No IEEPA tariffs found in this run.'
      ) {
        const emptyMessage =
          rulesMode === 'eligible' || rulesMode === 'phase_2'
            ? 'No applicable IEEPA transactions were found. You may be missing some required columns, please check your original document'
            : rulesMode === 'phase_2_rollup'
            ? 'No claimable IEEPA entries were found to roll up.'
            : rulesMode === 'ineligible' || rulesMode === 'phase_2_exclusions'
            ? 'No IEEPA rows were excluded — every IEEPA row in this run passes all eligibility rules.'
            : 'No IEEPA tariffs were found on any row in this run.';
        enqueueSnackbar(emptyMessage, { variant: 'info' });
        return;
      }

      if (!url || !fileName) {
        throw new Error(
          'Invalid download URL or file name returned from server.',
        );
      }

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      enqueueSnackbar(`${reportLabel} generated (${totalOutputRows} rows)`, {
        variant: 'success',
      });
      setShowIeepaModal(false);
    } catch (err: any) {
      console.error(err);
      enqueueSnackbar('Failed to generate IEEPA report.', {
        variant: 'error',
      });
    } finally {
      setIsGeneratingIeepa(false);
    }
  };

  /**
   * Download the BITE Findings Report (PDF) for this run. The PDF was built
   * by the automations processor at finalize time and lives in S3 next to
   * the CSV exports — this handler just presigns the existing object and
   * triggers a browser download. No server-side regeneration.
   *
   * Filename format matches the AceAnalyzerPage actions-column download so
   * the same file lands on disk regardless of where the user clicked from:
   *   "BITE Report {Run Name} - {MM-DD-YYYY}.pdf"
   */
  const handleDownloadBiteReport = async (): Promise<void> => {
    if (!runId) return;
    if (
      runDetails?.status !== 'Completed' &&
      runDetails?.status !== 'Complete' &&
      runDetails?.status !== 'Needs Review'
    ) {
      enqueueSnackbar(
        'BITE Report is available once the analysis run is completed.',
        { variant: 'info' },
      );
      return;
    }

    setIsDownloadingBiteReport(true);
    try {
      const safeName = (runDetails?.name || 'Analysis').trim();
      const dateStr = runDetails?.created_at
        ? moment(runDetails.created_at).format('MM-DD-YYYY')
        : moment().format('MM-DD-YYYY');
      const fileName = `BITE Report ${safeName} - ${dateStr}.pdf`;

      const { url } = await AnalyzersAPI.getBiteReportDownloadUrl(
        runId,
        fileName,
      );

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (err) {
      console.error('Failed to download BITE Report:', err);
      enqueueSnackbar(
        'Could not download the BITE Report. Re-Run the analysis if the issue persists.',
        { variant: 'error' },
      );
    } finally {
      setIsDownloadingBiteReport(false);
    }
  };

  // Any narrowing currently in effect, beyond the tab itself. Drives the
  // export modal's "Filters: Applied/None" line and the over-limit nudge.
  const exportHasActiveFilters = Boolean(
    debouncedSearch ||
      activeTariffFilters.length ||
      activeFindingFilters.length ||
      activeCountryFilters.length,
  );
  // Default export file name (without extension): "<ticket name>_results_table".
  // Used as the modal input's placeholder and the fallback when left blank.
  const exportDefaultFileName = `${
    runDetails?.name || 'ACE_Run'
  }_results_table`;

  // On-demand CSV export of the current table view. Fetches EVERY row matching
  // the active tab + filters + search in one page (capped at
  // CSV_EXPORT_ROW_LIMIT), then builds the CSV in-browser. Columns mirror the
  // table up to FLAGS; the FLAGS column is expanded into one "Flag N" column
  // per finding, each holding the WHAT WE DETECTED text ("<type>: <message>").
  const handleExportTableCsv = async (customName?: string): Promise<void> => {
    if (!runId) return;
    const notifyOverLimit = (total: number) => {
      enqueueSnackbar(
        `Too many rows to export (${total.toLocaleString()}). Narrow to ${CSV_EXPORT_ROW_LIMIT.toLocaleString()} or fewer using the tabs and filters.`,
        { variant: 'warning' },
      );
    };
    // The modal already blocks these, but guard here too in case the handler
    // is reached with a stale count.
    if (totalLines === 0) {
      enqueueSnackbar('There are no rows to export in this view.', {
        variant: 'info',
      });
      return;
    }
    if (totalLines > CSV_EXPORT_ROW_LIMIT) {
      notifyOverLimit(totalLines);
      return;
    }

    setIsExporting(true);
    try {
      const baseHeaders = [
        'Entry #',
        'Line #',
        'Entry Date',
        'Importer',
        'HTS',
        'Description',
        'COO',
        'SPI',
        'Value',
        'Duty',
      ];

      let headerRow: string[] = [];
      let dataRows: (string | number)[][] = [];

      if (activeTab === 'Unmatched Rows') {
        const data = await AnalyzersAPI.getAceUnmatchedRows(
          runId,
          1,
          CSV_EXPORT_ROW_LIMIT,
          debouncedSearch || undefined,
          sortConfig.key || undefined,
          sortConfig.key ? sortConfig.direction : undefined,
        );
        // Authoritative recheck: the up-front guard used `totalLines`, which can
        // be stale if the user changed tabs/filters and the new view's fetch
        // hasn't landed yet. `data.total` is the true count for THIS query, so
        // bail instead of silently saving the first CSV_EXPORT_ROW_LIMIT rows.
        if ((data.total ?? 0) > CSV_EXPORT_ROW_LIMIT) {
          notifyOverLimit(data.total ?? 0);
          return;
        }
        const rows = data.rows || [];
        // Unmatched rows carry no findings — their "flag" equivalent is the
        // anomaly reason, so we expose a single column for it.
        headerRow = [...baseHeaders, 'Unmatched Reason'];
        dataRows = rows.map((item: any) => [
          item['Entry Summary Number'] || '',
          item['Entry Summary Line Number'] || '',
          item['Entry Date']
            ? moment.utc(item['Entry Date']).format('MM/DD/YYYY')
            : '',
          item['Importer Name'] || '',
          item['HTS Number - Full'] || item['HTS Number'] || '',
          (item['HTS Short Description'] || '').toLowerCase(),
          item['Country of Origin Code'] || '',
          item['Line SPI Code'] || '',
          csvCurrency(item['Line Tariff Goods Value Amount']),
          csvCurrency(item['Line Tariff Duty Amount']),
          item.Anomaly_Reason ? `Unmatched: ${item.Anomaly_Reason}` : '',
        ]);
      } else {
        const data = await AnalyzersAPI.getAceRunLines(
          runId,
          1,
          CSV_EXPORT_ROW_LIMIT,
          activeTab,
          debouncedSearch,
          activeTariffFilters.join(','),
          activeFindingFilters.join(','),
          activeCountryFilters.join(','),
          sortConfig.key || undefined,
          sortConfig.key ? sortConfig.direction : undefined,
        );
        // Authoritative recheck against the true count for THIS query — see the
        // unmatched branch above. Prevents a stale `totalLines` from letting a
        // >10k view export only its first CSV_EXPORT_ROW_LIMIT rows as "success".
        if ((data.total ?? 0) > CSV_EXPORT_ROW_LIMIT) {
          notifyOverLimit(data.total ?? 0);
          return;
        }
        const rows = data.lines || [];
        const built = rows.map((item: any) => {
          const flags = parseFlagsForExport(item.flags).map(flagToDetectedText);
          const base: (string | number)[] = [
            item.entry_summary_number || '',
            Number(item.entry_summary_line_number) || '',
            item.entry_date
              ? moment.utc(item.entry_date).format('MM/DD/YYYY')
              : '',
            item.importer_name || '',
            item.hts_number_full || item.ch98_hts2 || '',
            (item.hts_short_description || item.ch98_hts2_desc || '')
              .toString()
              .toLowerCase(),
            item.country_of_origin_code || '',
            item.line_spi_code || '',
            csvCurrency(
              item.line_tariff_goods_value_amount || item.ch98_hts2_value,
            ),
            csvCurrency(item.total_duty, true),
          ];
          return { base: base, flags: flags };
        });
        // Widen to the busiest row so every row has the same column count.
        const maxFlags = built.reduce(
          (max: number, r: { flags: string[] }) =>
            Math.max(max, r.flags.length),
          0,
        );
        const flagHeaders = Array.from(
          { length: maxFlags },
          (_, i) => `Flag ${i + 1}`,
        );
        headerRow = [...baseHeaders, ...flagHeaders];
        dataRows = built.map(
          (r: { base: (string | number)[]; flags: string[] }) => [
            ...r.base,
            ...r.flags,
            ...Array(maxFlags - r.flags.length).fill(''),
          ],
        );
      }

      const csv = [headerRow, ...dataRows]
        .map((row) => row.map(csvCell).join(','))
        .join('\r\n');
      // Lead with a UTF-8 BOM so Excel renders arrows / em-dashes in finding
      // text correctly instead of mojibake.
      const blob = new Blob(['\uFEFF', csv], {
        type: 'text/csv;charset=utf-8;',
      });
      // User-provided name wins; blank falls back to the ticket-based default.
      // Sanitize to filesystem-safe characters and append the extension here so
      // the modal never has to deal with ".csv".
      const baseName =
        (customName && customName.trim()) || exportDefaultFileName;
      const safeName = baseName.replace(/[^\w.-]+/g, '_');
      saveAs(blob, `${safeName}.csv`);

      enqueueSnackbar(
        `Exported ${dataRows.length.toLocaleString()} row${
          dataRows.length === 1 ? '' : 's'
        } to CSV.`,
        { variant: 'success' },
      );
      setShowExportModal(false);
    } catch (error) {
      console.error('Failed to export table CSV:', error);
      enqueueSnackbar('Failed to export CSV. Please try again.', {
        variant: 'error',
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Helper to get data source based on tab
  const getActiveData = () => {
    if (activeTab === 'Unmatched Rows') return unmatchedLines;
    return lines;
  };

  const activeData = getActiveData();

  const isProcessing =
    runDetails?.status === 'Running' || runDetails?.status === 'Pending';

  return (
    <div className='analyzers-dashboard-root'>
      {/* Header */}
      <div style={{ marginBottom: '-20px' }}>
        <ToolBreadcrumb
          items={[
            { label: 'ACE Analyzer', to: '/ace-analyzers' },
            { label: runDetails?.name || 'Loading...' },
          ]}
        />
      </div>

      <div className='detail-header-flex'>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 className='analyzers-page-title' style={{ marginBottom: 0 }}>
              {runDetails?.name || 'Loading...'}
            </h1>
            <span
              className={`run-badge ${
                runDetails?.status?.toLowerCase() === 'completed'
                  ? 'completed'
                  : runDetails?.status?.toLowerCase() === 'needs review'
                  ? 'needs-review'
                  : runDetails?.status?.toLowerCase() === 'failed'
                  ? 'failed'
                  : ''
              }`}
              style={{
                marginTop: '2px',
                backgroundColor:
                  runDetails?.status === 'Failed' ? '#fee2e2' : undefined,
                color: runDetails?.status === 'Failed' ? '#dc2626' : undefined,
              }}
            >
              {runDetails?.status || '--'}
            </span>
            {/* Status edit pencil — only shown for settled runs so the user
                can flip between 'Completed' and 'Needs Review' without
                touching mid-process runs. Opens a MUI Menu anchored on the
                button; selection fires updateAceRunStatus and patches
                runDetails in place. */}
            {(runDetails?.status === 'Completed' ||
              runDetails?.status === 'Complete' ||
              runDetails?.status === 'Needs Review') && (
              <>
                <Tooltip title='Edit status'>
                  <span>
                    <button
                      onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
                      disabled={updatingStatus}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-secondary)',
                        cursor: updatingStatus ? 'not-allowed' : 'pointer',
                        padding: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: '2px',
                      }}
                      aria-label='Edit status'
                    >
                      <EditIcon style={{ fontSize: 16 }} />
                    </button>
                  </span>
                </Tooltip>
                <Menu
                  anchorEl={statusMenuAnchor}
                  open={!!statusMenuAnchor}
                  onClose={() => setStatusMenuAnchor(null)}
                >
                  <MenuItem
                    selected={
                      runDetails?.status === 'Completed' ||
                      runDetails?.status === 'Complete'
                    }
                    onClick={() => handleStatusChange('Completed')}
                  >
                    Completed
                  </MenuItem>
                  <MenuItem
                    selected={runDetails?.status === 'Needs Review'}
                    onClick={() => handleStatusChange('Needs Review')}
                  >
                    Needs Review
                  </MenuItem>
                </Menu>
              </>
            )}
          </div>
          <div className='run-detail-header-meta'>
            ACE •{' '}
            {runDetails?.created_at
              ? moment(runDetails.created_at).format('MM/DD/YYYY')
              : '--'}{' '}
            • {runDetails?.findings_count || 0} findings
            {/* "Ran By" attribution. Stamped at create time on the run row
                (initiated_by_name), preserved across re-runs. Only renders
                when the column is populated so legacy runs predating the
                migration don't show a "by --" segment. */}
            {runDetails?.initiated_by_name && (
              <> • {runDetails.initiated_by_name}</>
            )}
            {/*
              Credit cost from the planner's exact distinct-Entry-Summary
              count. Appears as soon as the planner finishes (well before
              finalize). While the planner is still running, the field is
              NULL and we just don't render the segment.
            */}
            {runDetails?.estimated_credits !== null &&
              runDetails?.estimated_credits !== undefined && (
                <>
                  {' '}
                  •{' '}
                  <span title='Credits charged for this run (1 per unique Entry Summary Number).'>
                    {Number(runDetails.estimated_credits).toLocaleString()}{' '}
                    credits
                  </span>
                </>
              )}
          </div>
        </div>
        {/* Wrapping is opted into here rather than on the shared
            .detail-header-actions class, which other pages rely on staying a
            single row — this row carries a toggle plus five buttons and would
            otherwise overflow on a narrow window. */}
        <div className='detail-header-actions' style={{ flexWrap: 'wrap' }}>
          <button
            className='btn-outline'
            onClick={() => setShowDownloadModal(true)}
            disabled={isProcessing}
            style={{
              opacity: isProcessing ? 0.5 : 1,
              cursor: isProcessing ? 'not-allowed' : 'pointer',
            }}
          >
            <IosShareIcon style={{ fontSize: 18 }} /> Data Export
          </button>
          {/* Create Report consolidates the IEEPA/BITE downloads and the AI
              Report Builder shortcut under one menu instead of three
              separate buttons. Each item calls the exact same handler its
              old standalone button did. */}
          <button
            className='btn-outline'
            onClick={(e) => setReportMenuAnchor(e.currentTarget)}
            disabled={isProcessing || isReRunning}
            style={{
              opacity: isProcessing || isReRunning ? 0.5 : 1,
              cursor: isProcessing || isReRunning ? 'not-allowed' : 'pointer',
            }}
          >
            <SummarizeOutlinedIcon style={{ fontSize: 18 }} /> Create Report
          </button>
          <Menu
            anchorEl={reportMenuAnchor}
            open={!!reportMenuAnchor}
            onClose={() => setReportMenuAnchor(null)}
            PaperProps={{
              sx: {
                mt: 1,
                bgcolor: 'var(--bg-colors)',
                color: 'var(--text-primary-color)',
                border: '1px solid var(--es-soft-border)',
                '& .MuiMenuItem-root:hover': {
                  backgroundColor: 'var(--bg-hover)',
                },
              },
            }}
          >
            <MenuItem
              disabled={isDownloadingBiteReport}
              onClick={() => {
                setReportMenuAnchor(null);
                handleDownloadBiteReport();
              }}
            >
              <PictureAsPdfOutlinedIcon
                style={{ fontSize: 18, marginRight: 8, color: '#4895ef' }}
              />
              {isDownloadingBiteReport ? (
                <CircularProgress size={14} color='inherit' style={{ marginRight: 8 }} />
              ) : null}
              Download BITE Report (PDF)
            </MenuItem>
            <MenuItem
              disabled={isGeneratingIeepa}
              onClick={() => {
                setReportMenuAnchor(null);
                setIeepaDownloadOpts('cape_phase_1');
                setIeepaFileName('');
                setShowIeepaModal(true);
              }}
            >
              <FileDownloadOutlinedIcon
                style={{ fontSize: 18, marginRight: 8, color: '#10b981' }}
              />
              Download IEEPA Report (CSV)...
            </MenuItem>
            <MenuItem
              onClick={() => {
                setReportMenuAnchor(null);
                history.push('/report-builder', {
                  sourceRun: {
                    type: 'ace',
                    runId: runId,
                    label: runDetails?.name || 'this ACE run',
                  },
                });
              }}
            >
              <SummarizeOutlinedIcon style={{ fontSize: 18, marginRight: 8 }} />
              Build Custom Report
            </MenuItem>
          </Menu>
          <button
            className='btn-primary'
            onClick={() => setShowReRunModal(true)}
            disabled={isProcessing || !runDetails?.original_s3_file_key}
            style={{
              opacity:
                isProcessing || !runDetails?.original_s3_file_key ? 0.5 : 1,
              cursor:
                isProcessing || !runDetails?.original_s3_file_key
                  ? 'not-allowed'
                  : 'pointer',
            }}
          >
            <ReplayIcon style={{ fontSize: 18 }} />{' '}
            {isProcessing ? 'Processing...' : 'Re-Run'}
          </button>
          <button
            className='btn-outline'
            onClick={() => setDeleteModalOpen(true)}
            disabled={isProcessing}
            style={{
              borderColor: '#ef4444',
              color: '#ef4444',
              opacity: isProcessing ? 0.5 : 1,
              cursor: isProcessing ? 'not-allowed' : 'pointer',
            }}
          >
            <DeleteOutlineIcon style={{ fontSize: 18 }} />
          </button>
        </div>
      </div>

      {/* KPI Grid */}
      <div className='detail-kpi-grid'>
        <div className='detail-kpi-card'>
          <div className='kpi-card-header'>
            <DescriptionIcon style={{ fontSize: 16, color: '#4895ef' }} />
            <span className='kpi-card-label'>Line Items</span>
          </div>
          <div className='kpi-card-value text-blue'>
            {(runDetails?.total_lines || 0).toLocaleString()}
          </div>
        </div>
        <div className='detail-kpi-card'>
          <div className='kpi-card-header'>
            <WarningAmberIcon style={{ fontSize: 16, color: '#f79009' }} />
            <span className='kpi-card-label'>Findings</span>
          </div>
          <div className='kpi-card-value text-orange'>
            {runDetails?.findings_count || 0}
          </div>
        </div>
        <div className='detail-kpi-card'>
          <div className='kpi-card-header'>
            <AttachMoneyIcon style={{ fontSize: 16, color: '#20c997' }} />
            <span className='kpi-card-label'>Total Value</span>
          </div>
          <div className='kpi-card-value text-teal'>
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              notation: 'compact',
              maximumFractionDigits: 1,
            }).format(runDetails?.total_value || 0)}
          </div>
        </div>
        <div className='detail-kpi-card'>
          <div className='kpi-card-header'>
            <AccountBalanceIcon style={{ fontSize: 16, color: '#17b26a' }} />
            <span className='kpi-card-label'>Total Duty</span>
          </div>
          <div className='kpi-card-value' style={{ color: '#17b26a' }}>
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              notation: 'compact',
              maximumFractionDigits: 1,
            }).format(runDetails?.total_duty || 0)}
          </div>
        </div>
      </div>

      {/* Data Section */}
      <div className='analyzers-section' style={{ marginTop: '10px' }}>
        <div className='analyzers-section-header'>
          <h2 className='section-title'>Uploaded Data</h2>
          <p className='section-subtitle'>
            Your entry line items with analysis results. Click any flagged row
            to view finding details.
          </p>
        </div>

        <div className='data-toolbar-container'>
          <div className='data-search-wrapper'>
            <SearchIcon className='data-search-icon' style={{ fontSize: 18 }} />
            <input
              type='text'
              className='data-search-input'
              placeholder='Search entries, HTS, importer, flags...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={isProcessing}
              style={{ cursor: isProcessing ? 'not-allowed' : 'text' }}
            />
          </div>

          <div className='data-segmented-control'>
            <button
              className={`data-segment-btn ${
                activeTab === 'All Lines' ? 'active' : ''
              }`}
              onClick={() => setActiveTab('All Lines')}
              disabled={isProcessing}
              style={{
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                opacity: isProcessing ? 0.6 : 1,
              }}
            >
              All Lines{' '}
              <span className='data-segment-count'>
                {runDetails?.total_lines || 0}
              </span>
            </button>
            <button
              className={`data-segment-btn ${
                activeTab === 'Flagged Lines' ? 'active' : ''
              }`}
              onClick={() => setActiveTab('Flagged Lines')}
              disabled={isProcessing}
              style={{
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                opacity: isProcessing ? 0.6 : 1,
              }}
            >
              Flagged{' '}
              <span className='data-segment-count'>
                {runDetails?.flagged_lines || 0}
              </span>
            </button>
            <button
              className={`data-segment-btn ${
                activeTab === 'Cleared Lines' ? 'active' : ''
              }`}
              onClick={() => setActiveTab('Cleared Lines')}
              disabled={isProcessing}
              style={{
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                opacity: isProcessing ? 0.6 : 1,
              }}
            >
              Cleared{' '}
              <span className='data-segment-count'>
                {runDetails?.cleared_lines || 0}
              </span>
            </button>
            <button
              className={`data-segment-btn ${
                activeTab === 'Unmatched Rows' ? 'active' : ''
              }`}
              onClick={() => setActiveTab('Unmatched Rows')}
              disabled={isProcessing}
              style={{
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                opacity: isProcessing ? 0.6 : 1,
              }}
            >
              Unmatched{' '}
              <span className='data-segment-count'>
                {runDetails?.unmatched_count ?? 0}
              </span>
            </button>
          </div>

          {/* Filters + Bulk Actions + Export — right-justified toolbar above
              the table (marginLeft:auto). Filters / Bulk Actions stay hidden on
              the Unmatched Rows tab; Export Table sits to the far right and is
              available on every tab (the export itself respects the active
              tab). All three share the .data-segment-btn look. */}
          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {activeTab !== 'Unmatched Rows' &&
              (() => {
                const activeFilterCount =
                  activeTariffFilters.length +
                  activeFindingFilters.length +
                  activeCountryFilters.length;
                const hasActiveFilters = activeFilterCount > 0;
                return (
                  <>
                    <button
                      className={`data-segment-btn`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        // Color matches Bulk Actions when no filters are
                        // applied (both use the .data-segment-btn default
                        // var(--text-secondary)); pops to the action blue
                        // when filters are active so the chip count reads
                        // as the "currently filtered" state at a glance.
                        color: hasActiveFilters
                          ? '#4895EF'
                          : 'var(--text-secondary)',
                        cursor: isProcessing ? 'not-allowed' : 'pointer',
                        opacity: isProcessing ? 0.6 : 1,
                      }}
                      onClick={() => setShowFilterModal(true)}
                      disabled={isProcessing}
                    >
                      <FilterListIcon style={{ fontSize: 16 }} />
                      Filters {hasActiveFilters ? `(${activeFilterCount})` : ''}
                    </button>
                    {/* Clear-all-filters quick action. Only renders when at
                        least one filter is active so the toolbar stays
                        clean. Single click drops every filter array; the
                        fetch-lines effect re-pulls on the next tick. */}
                    {hasActiveFilters && (
                      <Tooltip title='Clear Filters'>
                        <button
                          className={`data-segment-btn`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '6px',
                            color: 'var(--text-secondary)',
                            cursor: isProcessing ? 'not-allowed' : 'pointer',
                            opacity: isProcessing ? 0.6 : 1,
                          }}
                          onClick={() => {
                            setActiveTariffFilters([]);
                            setActiveFindingFilters([]);
                            setActiveCountryFilters([]);
                            setCurrentPage(1);
                          }}
                          disabled={isProcessing}
                          aria-label='Clear Filters'
                        >
                          <CloseIcon style={{ fontSize: 16 }} />
                        </button>
                      </Tooltip>
                    )}
                    <button
                      className={`data-segment-btn`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: 'var(--text-secondary)',
                        cursor: isProcessing ? 'not-allowed' : 'pointer',
                        opacity: isProcessing ? 0.6 : 1,
                      }}
                      onClick={() => setShowBulkActionsModal(true)}
                      disabled={isProcessing}
                      title='Clear, restore, or comment on every row matching a filter'
                    >
                      <LayersIcon style={{ fontSize: 16 }} />
                      Bulk Actions
                    </button>
                  </>
                );
              })()}
            <ColumnPicker
              columns={ACE_COLUMNS}
              visible={visibleColumnKeys}
              onChange={handleColumnsChange}
              maxVisible={ACE_MAX_VISIBLE_COLUMNS}
            />
            {/* Export Table (CSV) — far right of Filters / Bulk Actions and
                styled like them. Outside the Unmatched-Rows gate above so it's
                available on every tab. */}
            <button
              className='data-segment-btn'
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: 'var(--text-secondary)',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                opacity: isProcessing ? 0.6 : 1,
              }}
              onClick={() => setShowExportModal(true)}
              disabled={isProcessing}
              title='Export the rows currently shown (with filters applied) to CSV'
            >
              <FileDownloadOutlinedIcon style={{ fontSize: 16 }} />
              Export Table (CSV)
            </button>
          </div>
        </div>

        <div className='runs-table-container acp-scroll-x'>
          <table className='run-data-table'>
            <thead>
              <tr>
                <th style={{ width: '40px' }}></th>
                <th
                  onClick={() => handleSort('entry_summary_number')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    ENTRY # {renderSortIndicator('entry_summary_number')}
                  </div>
                </th>
                <th style={{ width: '40px' }}>LN</th>
                {shownColumns.map((column) =>
                  column.sortKey ? (
                    <th
                      key={column.key}
                      onClick={() => handleSort(column.sortKey as string)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        {column.header} {renderSortIndicator(column.sortKey)}
                      </div>
                    </th>
                  ) : (
                    <th key={column.key}>{column.header}</th>
                  ),
                )}
                <th>FLAGS</th>
              </tr>
            </thead>
            <tbody>
              <style>
                {`
                  @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                  }
                `}
              </style>

              {loading ? (
                <tr>
                  <td
                    colSpan={tableColSpan}
                    style={{ textAlign: 'center', padding: '60px 40px' }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '16px',
                        color: '#6b7280',
                      }}
                    >
                      <svg
                        viewBox='0 0 24 24'
                        fill='none'
                        style={{
                          width: '36px',
                          height: '36px',
                          animation: 'spin 1s linear infinite',
                        }}
                      >
                        <circle
                          cx='12'
                          cy='12'
                          r='10'
                          stroke='currentColor'
                          strokeWidth='4'
                          strokeOpacity='0.25'
                        />
                        <path
                          d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                          fill='#4895ef'
                        />
                      </svg>
                      Loading entry lines...
                    </div>
                  </td>
                </tr>
              ) : isProcessing && activeData.length === 0 ? (
                <tr>
                  <td
                    colSpan={tableColSpan}
                    style={{ textAlign: 'center', padding: '60px 40px' }}
                  >
                    {(() => {
                      const totalCh = Number(runDetails?.total_chunks) || 0;
                      const doneCh = Number(runDetails?.completed_chunks) || 0;
                      const pct =
                        Number(runDetails?.progress_percent) ||
                        (totalCh > 0
                          ? Math.min(99, Math.round((doneCh / totalCh) * 100))
                          : 0);
                      const isIndeterminate = totalCh === 0;
                      return (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '14px',
                            color: '#6b7280',
                            maxWidth: '520px',
                            margin: '0 auto',
                          }}
                        >
                          <style>
                            {`
                              @keyframes ace-progress-indeterminate {
                                0%   { left: -40%; width: 40%; }
                                60%  { left: 100%; width: 40%; }
                                100% { left: 100%; width: 40%; }
                              }
                            `}
                          </style>
                          <div style={{ width: '100%', textAlign: 'left' }}>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'baseline',
                                marginBottom: '8px',
                              }}
                            >
                              <span
                                style={{
                                  fontSize: '15px',
                                  fontWeight: 600,
                                  color: 'var(--text-primary, #111827)',
                                }}
                              >
                                {isIndeterminate
                                  ? 'Queuing chunks...'
                                  : `Processing ${doneCh.toLocaleString()} of ${totalCh.toLocaleString()} chunks`}
                              </span>
                              <span
                                style={{
                                  fontSize: '14px',
                                  fontWeight: 600,
                                  color: '#4895ef',
                                  fontVariantNumeric: 'tabular-nums',
                                }}
                              >
                                {isIndeterminate ? '' : `${pct}%`}
                              </span>
                            </div>
                            <div
                              style={{
                                position: 'relative',
                                height: '8px',
                                width: '100%',
                                backgroundColor: 'rgba(72, 149, 239, 0.15)',
                                borderRadius: '999px',
                                overflow: 'hidden',
                              }}
                            >
                              {isIndeterminate ? (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    height: '100%',
                                    backgroundColor: '#4895ef',
                                    borderRadius: '999px',
                                    animation:
                                      'ace-progress-indeterminate 1.6s ease-in-out infinite',
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    height: '100%',
                                    width: `${pct}%`,
                                    backgroundColor: '#4895ef',
                                    borderRadius: '999px',
                                    transition:
                                      'width 500ms cubic-bezier(0.4, 0, 0.2, 1)',
                                  }}
                                />
                              )}
                            </div>
                          </div>
                          <span
                            style={{
                              fontSize: '13px',
                              fontStyle: 'italic',
                              color: '#6b7280',
                            }}
                          >
                            {loadingText}
                          </span>
                          {runDetails?.processing_errors > 0 && (
                            <span
                              style={{
                                fontSize: '12px',
                                color: '#b45309',
                                fontWeight: 500,
                              }}
                            >
                              {runDetails.processing_errors.toLocaleString()}{' '}
                              row{runDetails.processing_errors === 1 ? '' : 's'}{' '}
                              flagged with processing errors so far
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                </tr>
              ) : activeData.length === 0 ? (
                <tr>
                  <td
                    colSpan={tableColSpan}
                    style={{
                      textAlign: 'center',
                      padding: '60px 40px',
                      color: '#6b7280',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <SearchIcon style={{ fontSize: 32, opacity: 0.3 }} />
                      <span style={{ fontSize: '14px' }}>
                        No lines found for this filter.
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                activeData.map((item, index) => {
                  if (activeTab === 'Unmatched Rows') {
                    return (
                      <tr key={`unmatched-${item.Anomaly_ID || index}`}>
                        <td
                          style={{
                            verticalAlign: 'middle',
                            width: '40px',
                            textAlign: 'center',
                          }}
                        >
                          <ErrorOutlineIcon
                            style={{ color: '#ef4444', fontSize: 18 }}
                          />
                        </td>
                        <td style={{ verticalAlign: 'middle' }}>
                          {item['Entry Summary Number'] || '--'}
                        </td>
                        <td style={{ verticalAlign: 'middle' }}>
                          {item['Entry Summary Line Number'] || '--'}
                        </td>
                        {shownColumns.map((column) => (
                          <td
                            key={column.key}
                            style={{ verticalAlign: 'middle', ...column.cellStyle }}
                          >
                            {column.renderUnmatched(item)}
                          </td>
                        ))}
                        <td style={{ verticalAlign: 'middle', padding: '8px' }}>
                          <span
                            style={{
                              backgroundColor: '#fee2e2',
                              color: '#991b1b',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Unmatched: {item.Anomaly_Reason}
                          </span>
                        </td>
                      </tr>
                    );
                  }

                  const flagsArray =
                    typeof item.flags === 'string'
                      ? JSON.parse(item.flags)
                      : item.flags || [];
                  // Cleared signatures from the LEFT-JOINed user_data row.
                  // Used both to dim/strike chips below and to drive the
                  // status dot — a row whose findings have all been
                  // manually dismissed should read as compliant from the
                  // table, just like in the drawer.
                  const rowClearedMap = parseClearedFlags(item.cleared_flags);
                  const rowHasActive = flagsArray.some(
                    (f: any) => !rowClearedMap.has(flagSignature(f)),
                  );
                  const hasFlags = flagsArray.length > 0;

                  return (
                    <tr
                      key={item.id || index}
                      onClick={() => {
                        setSelectedLine({ ...item, parsedFlags: flagsArray });
                        setIsDrawerOpen(true);
                      }}
                      className={
                        selectedLine?.id === item.id ? 'selected-row' : ''
                      }
                      // Inline styles so the highlight is guaranteed to
                      // render regardless of the global stylesheet —
                      // there's no .selected-row rule defined anywhere
                      // in theme.css/index.css today, and the user wants
                      // the anchor to persist even after the drawer is
                      // closed. Left-border accent + subtle background
                      // tint reads clearly in both light and dark themes.
                      style={{
                        cursor: 'pointer',
                        ...(selectedLine?.id === item.id
                          ? {
                              backgroundColor: 'rgba(72, 149, 239, 0.12)',
                              borderLeft: '3px solid #4895ef',
                            }
                          : {
                              borderLeft: '3px solid transparent',
                            }),
                      }}
                    >
                      <td
                        style={{
                          verticalAlign: 'middle',
                          width: '40px',
                          textAlign: 'center',
                        }}
                      >
                        {rowHasActive && !item.is_reviewed ? (
                          <WarningAmberIcon
                            style={{
                              color: 'var(--red-color, #ef4444)',
                              fontSize: 18,
                            }}
                          />
                        ) : (
                          <div
                            className='status-dot clean'
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: '#22c55e',
                              margin: '0 auto',
                            }}
                          />
                        )}
                      </td>
                      <td style={{ verticalAlign: 'middle' }}>
                        {item.entry_summary_number || '--'}
                      </td>
                      <td style={{ verticalAlign: 'middle', width: '40px' }}>
                        {Number(item.entry_summary_line_number) || '--'}
                      </td>
                      {shownColumns.map((column) => (
                        <td
                          key={column.key}
                          style={{ verticalAlign: 'middle', ...column.cellStyle }}
                        >
                          {column.render(item)}
                        </td>
                      ))}
                      <td style={{ verticalAlign: 'middle', padding: '8px' }}>
                        {hasFlags ? (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '6px',
                              alignItems: 'flex-start',
                            }}
                          >
                            {flagsArray.map((f: any, i: number) => {
                              // Cleared chips read as dismissed at a glance
                              // without disappearing — same visual treatment
                              // as the drawer's FLAGS ON THIS LINE strip so
                              // the table and the drawer stay in sync.
                              const isCleared = rowClearedMap.has(
                                flagSignature(f),
                              );
                              return (
                                <span
                                  className={`screening-badge lens-chip ${flagChipClass(
                                    f,
                                  )}`}
                                  key={i}
                                  title={
                                    isCleared
                                      ? 'Manually cleared — open the line for details and to restore.'
                                      : undefined
                                  }
                                  style={
                                    isCleared
                                      ? {
                                          opacity: 0.5,
                                          textDecoration: 'line-through',
                                        }
                                      : undefined
                                  }
                                >
                                  {f.type}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <span style={{ color: '#9ca3af', fontSize: '12px' }}>
                            --
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {!loading && totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '20px',
              }}
            >
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color='primary'
              />
            </div>
          )}
        </div>
      </div>

      {/* --- SLIDE-OUT DRAWER OVERLAY --- */}
      {/*
        Gated on isDrawerOpen instead of just selectedLine so the row
        highlight in the table stays put after the drawer closes — the
        user can scan up/down without losing their navigation anchor.
      */}
      {selectedLine && isDrawerOpen && (
        <div className='drawer-backdrop' onClick={() => setIsDrawerOpen(false)}>
          <div className='drawer-panel' onClick={(e) => e.stopPropagation()}>
            <div className='drawer-header'>
              {/*
                Prev / next nav. Operate against the currently-loaded
                `lines` array. At page boundaries we change the page and
                stash a pendingDrawerNav marker; the fetch-lines effect
                handles selecting the right row once new data arrives.
              */}
              {(() => {
                const idx = lines.findIndex(
                  (l: any) => l.id === selectedLine?.id,
                );
                const atFirst = idx === 0 && currentPage === 1;
                const atLast =
                  idx === lines.length - 1 && currentPage >= totalPages;
                const canPrev =
                  idx >= 0 && !atFirst && pendingDrawerNav === null;
                const canNext =
                  idx >= 0 && !atLast && pendingDrawerNav === null;
                const navStyle: React.CSSProperties = {
                  background: 'transparent',
                  border: '1px solid var(--border-color, #374151)',
                  color: 'inherit',
                  borderRadius: '6px',
                  padding: '4px 6px',
                  display: 'inline-flex',
                  alignItems: 'center',
                };
                return (
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      style={{
                        ...navStyle,
                        opacity: canPrev ? 1 : 0.4,
                        cursor: canPrev ? 'pointer' : 'not-allowed',
                      }}
                      onClick={handleDrawerPrev}
                      disabled={!canPrev}
                      title='Open the previous line'
                    >
                      <ChevronLeftIcon style={{ fontSize: 18 }} />
                    </button>
                    <button
                      style={{
                        ...navStyle,
                        opacity: canNext ? 1 : 0.4,
                        cursor: canNext ? 'pointer' : 'not-allowed',
                      }}
                      onClick={handleDrawerNext}
                      disabled={!canNext}
                      title='Open the next line'
                    >
                      <ChevronRightIcon style={{ fontSize: 18 }} />
                    </button>
                  </div>
                );
              })()}
              <h2 className='drawer-title'>
                {selectedLine.entry_summary_number} • Line{' '}
                {selectedLine.entry_summary_line_number}
              </h2>
              <button
                className='drawer-close-btn'
                onClick={() => setIsDrawerOpen(false)}
              >
                <CloseIcon />
              </button>
            </div>

            <div className='drawer-body'>
              <div>
                <div className='drawer-section-title'>LINE ITEM DETAILS</div>
                <div className='drawer-kv-box'>
                  <div className='drawer-kv-row'>
                    <span className='drawer-kv-label'>Importer</span>
                    <span className='drawer-kv-value'>
                      {selectedLine.importer_name || '--'}
                    </span>
                  </div>
                  <div className='drawer-kv-row'>
                    <span className='drawer-kv-label'>HTS</span>
                    <span className='drawer-kv-value'>
                      {selectedLine.hts_number_full ||
                        selectedLine.ch98_hts2 ||
                        '--'}
                    </span>
                  </div>
                  <div className='drawer-kv-row'>
                    <span className='drawer-kv-label'>Description</span>
                    <span
                      className='drawer-kv-value'
                      style={{ fontFamily: 'Inter', fontWeight: 400 }}
                    >
                      {(
                        selectedLine.hts_short_description ||
                        selectedLine.ch98_hts2_desc
                      )?.toLowerCase() || '--'}
                    </span>
                  </div>
                  <div className='drawer-kv-row'>
                    <span className='drawer-kv-label'>Country of Origin</span>
                    <span className='drawer-kv-value'>
                      {selectedLine.country_of_origin_code || '--'}
                    </span>
                  </div>
                  <div className='drawer-kv-row'>
                    <span className='drawer-kv-label'>FTA (SPI)</span>
                    <span className='drawer-kv-value'>
                      {selectedLine.line_spi_code || '--'}
                    </span>
                  </div>
                  <div className='drawer-kv-row'>
                    <span className='drawer-kv-label'>Entry Date</span>
                    <span className='drawer-kv-value'>
                      {selectedLine.entry_date
                        ? moment
                            .utc(selectedLine.entry_date)
                            .format('MM/DD/YYYY')
                        : '--'}
                    </span>
                  </div>
                  {/* Only rendered when the upload carried an IT Date — an
                      absent column is not a missing value, so it gets no row
                      rather than a '--'. */}
                  {(() => {
                    const itDate = extractItDate(selectedLine);
                    if (!itDate) return null;
                    return (
                      <div className='drawer-kv-row'>
                        <span className='drawer-kv-label'>IT Date</span>
                        <span className='drawer-kv-value'>{itDate}</span>
                      </div>
                    );
                  })()}
                  <div className='drawer-kv-row'>
                    <span className='drawer-kv-label'>Value</span>
                    <span className='drawer-kv-value'>
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                      }).format(
                        selectedLine.line_tariff_goods_value_amount ||
                          selectedLine.ch98_hts2_value ||
                          0,
                      )}
                    </span>
                  </div>
                  <div className='drawer-kv-row'>
                    <span className='drawer-kv-label'>Duty Paid</span>
                    <span className='drawer-kv-value'>
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                      }).format(selectedLine.total_duty || 0)}
                    </span>
                  </div>
                  <div className='drawer-kv-row'>
                    <span className='drawer-kv-label'>Base Duty Paid</span>
                    <span className='drawer-kv-value'>
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                      }).format(selectedLine.line_tariff_duty_amount || 0)}
                    </span>
                  </div>
                  <div className='drawer-kv-row'>
                    <span className='drawer-kv-label'>MPF</span>
                    <span className='drawer-kv-value'>
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                      }).format(selectedLine.line_mpf_amount || 0)}
                    </span>
                  </div>
                  <div className='drawer-kv-row'>
                    <span className='drawer-kv-label'>HMF</span>
                    <span className='drawer-kv-value'>
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                      }).format(selectedLine.line_hmf_amount || 0)}
                    </span>
                  </div>
                </div>

                {(() => {
                  const tariffs = extractLineTariffs(selectedLine);
                  if (tariffs.length === 0) return null;

                  return (
                    <div style={{ marginTop: '20px' }}>
                      <div
                        style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          color: 'var(--text-secondary, #9ca3af)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          marginBottom: '8px',
                        }}
                      >
                        Applied Tariffs
                      </div>
                      <div
                        style={{
                          overflowX: 'auto',
                          borderRadius: '6px',
                          border: '1px solid var(--border-color, #374151)',
                        }}
                      >
                        <table
                          style={{
                            width: '100%',
                            fontSize: '13px',
                            borderCollapse: 'collapse',
                            textAlign: 'left',
                          }}
                        >
                          <thead
                            style={{
                              backgroundColor: 'rgba(0, 0, 0, 0.15)',
                              color: 'var(--text-secondary, #9ca3af)',
                            }}
                          >
                            <tr>
                              <th
                                style={{
                                  padding: '8px 12px',
                                  fontWeight: 500,
                                  borderBottom:
                                    '1px solid var(--border-color, #374151)',
                                  width: '60px',
                                  textAlign: 'center',
                                }}
                              >
                                #
                              </th>
                              <th
                                style={{
                                  padding: '8px 12px',
                                  fontWeight: 500,
                                  borderBottom:
                                    '1px solid var(--border-color, #374151)',
                                }}
                              >
                                Type
                              </th>
                              <th
                                style={{
                                  padding: '8px 12px',
                                  fontWeight: 500,
                                  borderBottom:
                                    '1px solid var(--border-color, #374151)',
                                }}
                              >
                                HTS
                              </th>
                              <th
                                style={{
                                  padding: '8px 12px',
                                  fontWeight: 500,
                                  borderBottom:
                                    '1px solid var(--border-color, #374151)',
                                }}
                              >
                                Description
                              </th>
                              <th
                                style={{
                                  padding: '8px 12px',
                                  fontWeight: 500,
                                  borderBottom:
                                    '1px solid var(--border-color, #374151)',
                                  textAlign: 'right',
                                }}
                              >
                                Duty
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {tariffs.map((t, idx) => (
                              <tr
                                key={idx}
                                style={{
                                  borderBottom:
                                    idx === tariffs.length - 1
                                      ? 'none'
                                      : '1px solid var(--border-color, #374151)',
                                }}
                              >
                                <td
                                  style={{
                                    padding: '8px 12px',
                                    whiteSpace: 'nowrap',
                                    textAlign: 'center',
                                  }}
                                >
                                  {t.ordinal || '--'}
                                </td>
                                <td
                                  style={{
                                    padding: '8px 12px',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {t.type}
                                </td>
                                <td
                                  style={{
                                    padding: '8px 12px',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {t.hts}
                                </td>
                                <td
                                  style={{
                                    padding: '8px 12px',
                                    maxWidth: '140px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                  title={t.desc}
                                >
                                  {t.desc.toLowerCase()}
                                </td>
                                <td
                                  style={{
                                    padding: '8px 12px',
                                    textAlign: 'right',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {new Intl.NumberFormat('en-US', {
                                    style: 'currency',
                                    currency: 'USD',
                                  }).format(t.duty)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Section 232 metal-content split — show the reviewer the actual
                  arithmetic behind the split, so a duty finding on this line is
                  auditable without re-deriving it by hand. Absent for lines with
                  no metal-content split. */}
              {(() => {
                const derivation = parseMetalDerivation(
                  selectedLine.metal_content_derivation,
                );
                if (!derivation || derivation.detail.length === 0) return null;

                // The ARTICLE total the derivation actually priced against.
                // line_tariff_goods_value_amount is the base row's own value,
                // which under CBP's two-line method is only the non-metal
                // remainder — quoting it would render nonsense arithmetic like
                // "duty assessed on $4.24 of the $5 line" next to a declared $28
                // of $33 content.
                const lineValue =
                  Number(derivation.articleValue) ||
                  Number(selectedLine.line_goods_value_amount) ||
                  Number(selectedLine.line_tariff_goods_value_amount) ||
                  0;

                return (
                  <div style={{ marginTop: '20px' }}>
                    <div className='drawer-section-title'>
                      METAL CONTENT SPLIT
                    </div>
                    <div className='drawer-text-block'>
                      {derivation.detail.map((d: any, i: number) => {
                        const resolved = !d.reason;
                        return (
                          <div
                            key={`mcd-${i}`}
                            style={{
                              marginBottom:
                                i < derivation.detail.length - 1 ? '12px' : 0,
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'baseline',
                                gap: '8px',
                                flexWrap: 'wrap',
                              }}
                            >
                              <span style={{ fontWeight: 500 }}>{d.label}</span>
                              {resolved ? (
                                <span
                                  className='screening-badge lens-chip chip-blue'
                                  style={{ fontFamily: 'Inter' }}
                                >
                                  {formatContentPct(d.pct)}
                                </span>
                              ) : (
                                <span className='screening-badge lens-chip chip-yellow'>
                                  Not determined
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                color: 'var(--text-secondary)',
                                marginTop: '2px',
                              }}
                            >
                              {resolved ? (
                                <>
                                  {describeContentBasis(d)}
                                  {lineValue > 0 && d.pct !== null && (
                                    <>
                                      {' — duty assessed on '}
                                      <strong style={{ fontFamily: 'Inter' }}>
                                        {formatUsd(
                                          (lineValue *
                                            Math.min(
                                              100,
                                              Math.max(0, d.pct),
                                            )) /
                                            100,
                                        )}
                                      </strong>
                                      {` of the ${formatUsd(lineValue)} line`}
                                    </>
                                  )}
                                  {d.overDeclared && (
                                    <div style={{ marginTop: '2px' }}>
                                      {describeOverDeclared(d)}
                                    </div>
                                  )}
                                </>
                              ) : (
                                METAL_GAP_HINT[
                                  d.reason as keyof typeof METAL_GAP_HINT
                                ] || 'The required inputs are not in the ACE export.'
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {(() => {
                // Compute the partition once per render: engine flags split
                // into active (still visible) vs cleared (dismissed by a user
                // via cleared_flags). Used by both the FLAGS chip row and
                // the WHAT WE DETECTED list below so the two stay in sync.
                const clearedMap = parseClearedFlags(
                  selectedLine.cleared_flags,
                );
                const allFlags = selectedLine.parsedFlags || [];
                const activeFlags: any[] = [];
                const clearedFlagsResolved: Array<{
                  flag: any;
                  entry: ClearedFlagEntry;
                }> = [];
                for (const f of allFlags) {
                  const sig = flagSignature(f);
                  const entry = clearedMap.get(sig);
                  if (entry)
                    clearedFlagsResolved.push({ flag: f, entry: entry });
                  else activeFlags.push(f);
                }

                return (
                  <>
                    {allFlags.length > 0 && (
                      <div>
                        <div className='drawer-section-title'>
                          FLAGS ON THIS LINE
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            flexWrap: 'wrap',
                          }}
                        >
                          {activeFlags.map((f: any, i: number) => (
                            <span
                              key={`active-${i}`}
                              className={`screening-badge lens-chip ${flagChipClass(
                                f,
                              )}`}
                            >
                              {f.type}
                            </span>
                          ))}
                          {clearedFlagsResolved.map(
                            ({ flag: f }, i: number) => (
                              <span
                                key={`cleared-${i}`}
                                className={`screening-badge lens-chip ${flagChipClass(
                                  f,
                                )}`}
                                title={`Cleared finding (preserved for review)`}
                                style={{
                                  opacity: 0.5,
                                  textDecoration: 'line-through',
                                }}
                              >
                                {f.type}
                              </span>
                            ),
                          )}
                        </div>
                      </div>
                    )}

                    <div>
                      <div className='drawer-section-title'>
                        WHAT WE DETECTED
                      </div>
                      <div className='drawer-text-block'>
                        {allFlags.length === 0 ? (
                          <span style={{ color: 'green', fontWeight: 500 }}>
                            No issues detected. This line appears compliant
                            based on our calculations.
                          </span>
                        ) : activeFlags.length === 0 ? (
                          // Every engine finding has been manually cleared.
                          // Show a banner naming who cleared (most-recent
                          // entry) so reviewers know the row was not silent
                          // from the engine — a user actively dismissed it.
                          <>
                            <div
                              style={{
                                color: '#10b981',
                                fontWeight: 500,
                                marginBottom: '8px',
                              }}
                            >
                              All findings on this line have been manually
                              cleared by{' '}
                              <strong>
                                {clearedFlagsResolved[
                                  clearedFlagsResolved.length - 1
                                ]?.entry?.clearedByName || 'a user'}
                              </strong>
                              {clearedFlagsResolved[
                                clearedFlagsResolved.length - 1
                              ]?.entry?.clearedAt
                                ? ` on ${moment(
                                    clearedFlagsResolved[
                                      clearedFlagsResolved.length - 1
                                    ].entry.clearedAt,
                                  ).format('MM/DD/YYYY')}`
                                : ''}
                              . Use Restore below to bring them back if needed.
                            </div>
                            <ul style={{ paddingLeft: '20px', margin: 0 }}>
                              {clearedFlagsResolved.map(
                                ({ flag: f, entry }, i: number) => (
                                  <li
                                    key={`cl-${i}`}
                                    style={{
                                      marginBottom: '8px',
                                      opacity: 0.6,
                                      textDecoration: 'line-through',
                                    }}
                                  >
                                    <strong>{f.type}:</strong> {f.message}{' '}
                                    <span
                                      style={{
                                        fontStyle: 'italic',
                                        opacity: 0.85,
                                        textDecoration: 'none',
                                      }}
                                    >
                                      (cleared by {entry.clearedByName}
                                      {entry.clearedAt
                                        ? ` on ${moment(entry.clearedAt).format(
                                            'MM/DD/YYYY',
                                          )}`
                                        : ''}
                                      )
                                    </span>{' '}
                                    <button
                                      onClick={() =>
                                        handleRestoreFindings([
                                          flagSignature(f),
                                        ])
                                      }
                                      disabled={isClearingFindings}
                                      style={{
                                        marginLeft: '6px',
                                        background: 'transparent',
                                        border: '1px solid #10b981',
                                        color: '#10b981',
                                        borderRadius: '4px',
                                        padding: '2px 8px',
                                        fontSize: '11px',
                                        cursor: isClearingFindings
                                          ? 'not-allowed'
                                          : 'pointer',
                                        textDecoration: 'none',
                                      }}
                                      title='Restore this finding'
                                    >
                                      Restore
                                    </button>
                                  </li>
                                ),
                              )}
                            </ul>
                          </>
                        ) : (
                          // Mixed state (some active, some cleared) or all
                          // active. Render active first with a per-row Clear
                          // button, then cleared with a per-row Restore.
                          <ul style={{ paddingLeft: '20px', margin: 0 }}>
                            {activeFlags.map((f: any, i: number) => (
                              <li
                                key={`a-${i}`}
                                style={{ marginBottom: '8px' }}
                              >
                                <strong>{f.type}:</strong> {f.message}{' '}
                                <button
                                  onClick={() =>
                                    handleClearFindings([flagSignature(f)])
                                  }
                                  disabled={isClearingFindings}
                                  style={{
                                    marginLeft: '6px',
                                    background: 'transparent',
                                    border: '1px solid #f04438',
                                    color: '#f04438',
                                    borderRadius: '4px',
                                    padding: '2px 8px',
                                    fontSize: '11px',
                                    cursor: isClearingFindings
                                      ? 'not-allowed'
                                      : 'pointer',
                                  }}
                                  title='Clear this finding'
                                >
                                  Clear
                                </button>
                              </li>
                            ))}
                            {clearedFlagsResolved.map(
                              ({ flag: f, entry }, i: number) => (
                                <li
                                  key={`c-${i}`}
                                  style={{
                                    marginBottom: '8px',
                                    opacity: 0.6,
                                    textDecoration: 'line-through',
                                  }}
                                >
                                  <strong>{f.type}:</strong> {f.message}{' '}
                                  <span
                                    style={{
                                      fontStyle: 'italic',
                                      opacity: 0.85,
                                      textDecoration: 'none',
                                    }}
                                  >
                                    (cleared by {entry.clearedByName}
                                    {entry.clearedAt
                                      ? ` on ${moment(entry.clearedAt).format(
                                          'MM/DD/YYYY',
                                        )}`
                                      : ''}
                                    )
                                  </span>{' '}
                                  <button
                                    onClick={() =>
                                      handleRestoreFindings([flagSignature(f)])
                                    }
                                    disabled={isClearingFindings}
                                    style={{
                                      marginLeft: '6px',
                                      background: 'transparent',
                                      border: '1px solid #10b981',
                                      color: '#10b981',
                                      borderRadius: '4px',
                                      padding: '2px 8px',
                                      fontSize: '11px',
                                      cursor: isClearingFindings
                                        ? 'not-allowed'
                                        : 'pointer',
                                      textDecoration: 'none',
                                    }}
                                    title='Restore this finding'
                                  >
                                    Restore
                                  </button>
                                </li>
                              ),
                            )}
                          </ul>
                        )}
                      </div>
                    </div>
                  </>
                );
              })()}

              {(() => {
                const expected = extractExpectedTariffs(selectedLine);
                if (expected.length === 0) return null;

                return (
                  <div style={{ marginTop: '16px' }}>
                    <div
                      style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        color: 'var(--text-secondary, #9ca3af)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: '8px',
                      }}
                    >
                      BITE Expected Tariffs
                    </div>
                    <div
                      style={{
                        overflowX: 'auto',
                        borderRadius: '6px',
                        border: '1px solid var(--border-color, #374151)',
                      }}
                    >
                      <table
                        style={{
                          width: '100%',
                          fontSize: '13px',
                          borderCollapse: 'collapse',
                          textAlign: 'left',
                        }}
                      >
                        <thead
                          style={{
                            backgroundColor: 'rgba(0, 0, 0, 0.15)',
                            color: 'var(--text-secondary, #9ca3af)',
                          }}
                        >
                          <tr>
                            <th
                              style={{
                                padding: '8px 12px',
                                fontWeight: 500,
                                borderBottom:
                                  '1px solid var(--border-color, #374151)',
                                width: '60px',
                                textAlign: 'center',
                              }}
                            >
                              #
                            </th>
                            <th
                              style={{
                                padding: '8px 12px',
                                fontWeight: 500,
                                borderBottom:
                                  '1px solid var(--border-color, #374151)',
                              }}
                            >
                              Type
                            </th>
                            <th
                              style={{
                                padding: '8px 12px',
                                fontWeight: 500,
                                borderBottom:
                                  '1px solid var(--border-color, #374151)',
                              }}
                            >
                              HTS
                            </th>
                            <th
                              style={{
                                padding: '8px 12px',
                                fontWeight: 500,
                                borderBottom:
                                  '1px solid var(--border-color, #374151)',
                              }}
                            >
                              Description
                            </th>
                            <th
                              style={{
                                padding: '8px 12px',
                                fontWeight: 500,
                                borderBottom:
                                  '1px solid var(--border-color, #374151)',
                                textAlign: 'right',
                              }}
                            >
                              Duty
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {expected.map((t, idx) => (
                            <tr
                              key={idx}
                              style={{
                                borderBottom:
                                  idx === expected.length - 1
                                    ? 'none'
                                    : '1px solid var(--border-color, #374151)',
                              }}
                            >
                              <td
                                style={{
                                  padding: '8px 12px',
                                  whiteSpace: 'nowrap',
                                  textAlign: 'center',
                                }}
                              >
                                {t.expectedOrdinal || '--'}
                              </td>
                              <td
                                style={{
                                  padding: '8px 12px',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {t.type}
                              </td>
                              <td
                                style={{
                                  padding: '8px 12px',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {t.hts}
                              </td>
                              <td
                                style={{
                                  padding: '8px 12px',
                                  maxWidth: '140px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                                title={t.desc}
                              >
                                {t.desc.toLowerCase()}
                              </td>
                              <td
                                style={{
                                  padding: '8px 12px',
                                  textAlign: 'right',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {new Intl.NumberFormat('en-US', {
                                  style: 'currency',
                                  currency: 'USD',
                                }).format(t.duty)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {selectedLine.parsedFlags?.length > 0 &&
                (() => {
                  const hasAdCvd = selectedLine.parsedFlags.some(
                    (f: any) => f?.type === 'Potential AD/CVD',
                  );
                  const hasNeedsTariff = selectedLine.parsedFlags.some(
                    (f: any) => f?.type === 'Needs Tariff Tool Review',
                  );

                  const actions: React.ReactNode[] = [];

                  // Potential AD/CVD — send the user to the Import Screening
                  // tool, pre-filtered to the AD CVD Commodities BITE Lens for
                  // this line's HTS + origin, to review the specific order(s)
                  // that matched. (Router-state deep link, mirroring the Tariff
                  // Tool insights panel's "Go to Screening" link.)
                  if (hasAdCvd) {
                    actions.push(
                      <span key='adcvd'>
                        This line&apos;s HTS and country of origin match an
                        active U.S. AD/CVD order, but no Antidumping or
                        Countervailing case number was filed. Review the specific
                        order(s) in the{' '}
                        <Link
                          to={{
                            pathname: '/import-screening',
                            state: {
                              primary: selectedLine.hts_number_full,
                              country:
                                selectedLine.country_of_origin_name ||
                                selectedLine.country_of_origin_code,
                              tradeType: 'US Import',
                              lenses: ['AD CVD Commodities'],
                              lists: [],
                            },
                          }}
                          style={{ color: '#4895ef', fontWeight: 500 }}
                        >
                          BITE Import Screening Tool
                        </Link>{' '}
                        using the <strong>AD CVD Commodities</strong> BITE Lens,
                        then confirm with your customs broker whether an
                        Antidumping or Countervailing case number should have
                        been filed on this entry line.
                      </span>,
                    );
                  }

                  // Needs Tariff Tool Review. When the blocker was an
                  // underivable metal-content split, the flag message already
                  // names the metal, the Chapter 99 code and the exact missing
                  // ACE column (describeMetalContentGaps) — and it is rendered
                  // verbatim above under WHAT WE DETECTED. So the remediation
                  // here says what to DO about it and does not restate the cause.
                  // The old copy blamed material composition / Country of Smelt
                  // / loading date generically, but the processor only ever
                  // declares material percent as defaulted (smelt country is
                  // assumed equal to origin, loading date equal to entry date),
                  // so naming those two was misleading.
                  if (hasNeedsTariff) {
                    const derivation = parseMetalDerivation(
                      selectedLine.metal_content_derivation,
                    );
                    const gapReasons: string[] = (derivation?.detail || [])
                      .filter((d: any) => d?.reason)
                      .map((d: any) => String(d.reason));

                    const fromFiling = gapReasons.some(
                      (r) =>
                        r === 'no_content_quantity' ||
                        r === 'no_common_unit' ||
                        r === 'no_base_quantity',
                    );

                    actions.push(
                      <span key='tariff'>
                        {fromFiling ? (
                          <>
                            The quantities this line filed are not sufficient to
                            work out the metal content share, so the Section 232
                            duty cannot be verified from the uploaded file alone.
                            Confirm the content quantity with your customs broker,
                            then re-check the line in the{' '}
                          </>
                        ) : (
                          <>
                            The ACE export does not carry every input the
                            applicable tariff rules need for this line, so we
                            cannot determine accuracy from the uploaded file
                            alone. Re-check this transaction in the{' '}
                          </>
                        )}
                        <a
                          href='/tariff-checker'
                          target='_blank'
                          rel='noopener noreferrer'
                          style={{ color: '#4895ef', fontWeight: 500 }}
                        >
                          BITE Tariff Tool
                        </a>{' '}
                        by entering the line&apos;s HTS, country of origin, entry
                        date, value and the metal content percentage to confirm
                        the expected tariffs.
                      </span>,
                    );
                  }

                  // Generic remediation when no special-case flag applies —
                  // unchanged from the prior behavior.
                  if (actions.length === 0) {
                    actions.push(
                      <span key='invoice'>
                        Review the original commercial invoice to verify country
                        of origin and declared values.
                      </span>,
                      <span key='broker'>
                        Confirm HTS classification and applicable Chapter 99
                        provisions with your customs broker.
                      </span>,
                      <span key='psc'>
                        If an error is confirmed, consider filing a Post Summary
                        Correction (PSC).
                      </span>,
                    );
                  }

                  return (
                    <div>
                      <div className='drawer-section-title'>
                        RECOMMENDED ACTIONS
                      </div>
                      <div className='drawer-action-list'>
                        {actions.map((node, i) => (
                          <div className='drawer-action-item' key={i}>
                            <div className='drawer-action-number'>{i + 1}</div>
                            {node}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

              <div
                className='drawer-comments-section'
                style={{ marginTop: '24px', paddingTop: '16px' }}
              >
                <div className='drawer-section-title'>NOTES & COMMENTS</div>

                <div
                  className='drawer-comments-list'
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    marginBottom: '16px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                  }}
                >
                  {!selectedLine.notes || selectedLine.notes.length === 0 ? (
                    <div
                      className='drawer-comments-empty'
                      style={{ fontSize: '13px', fontStyle: 'italic' }}
                    >
                      No notes added yet.
                    </div>
                  ) : (
                    selectedLine.notes.map((note: any, idx: number) => (
                      <div
                        key={idx}
                        className='drawer-comment-card'
                        style={{
                          padding: '10px',
                          borderRadius: '6px',
                          fontSize: '13px',
                        }}
                      >
                        <div
                          className='drawer-comment-header'
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: '4px',
                          }}
                        >
                          <span
                            className='drawer-comment-author'
                            style={{ fontWeight: 600 }}
                          >
                            {note.user}
                          </span>
                          <span
                            className='drawer-comment-timestamp'
                            style={{ fontSize: '11px', opacity: 0.7 }}
                          >
                            {moment(note.timestamp).format(
                              'MMM D, YYYY h:mm A',
                            )}
                          </span>
                        </div>
                        <div className='drawer-comment-text'>{note.text}</div>
                      </div>
                    ))
                  )}
                </div>

                <div
                  className='drawer-comment-input-wrapper'
                  style={{ display: 'flex', gap: '8px' }}
                >
                  <input
                    type='text'
                    className='drawer-comment-input'
                    placeholder='Add a note about this line...'
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      fontSize: '13px',
                    }}
                  />
                  <button
                    className='drawer-comment-submit-btn'
                    onClick={handleAddNote}
                    disabled={isSubmittingNote || !newNote.trim()}
                    style={{
                      borderRadius: '6px',
                      padding: '0 12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <SendIcon
                      className='drawer-comment-submit-icon'
                      style={{ fontSize: 16 }}
                    />
                  </button>
                </div>
              </div>

              <div
                className='drawer-footer-actions'
                style={{ marginTop: '24px' }}
              >
                <Tooltip title='Coming soon!' arrow placement='top'>
                  <span>
                    <button
                      className='drawer-btn-primary'
                      disabled
                      style={{ opacity: 0.6, cursor: 'not-allowed' }}
                    >
                      <SendIcon style={{ fontSize: 16 }} /> Send to Broker
                    </button>
                  </span>
                </Tooltip>

                {(() => {
                  // Compute the same active/cleared partition the WHAT WE
                  // DETECTED block uses — drives which of the two bulk
                  // buttons (Clear All / Restore All) to show. Both can
                  // appear simultaneously when the row is in a mixed
                  // state, so the user can quickly clear any remaining
                  // active findings AND restore the ones they dismissed.
                  const clearedMap = parseClearedFlags(
                    selectedLine.cleared_flags,
                  );
                  const allFlags = selectedLine.parsedFlags || [];
                  const hasActive = allFlags.some(
                    (f: any) => !clearedMap.has(flagSignature(f)),
                  );
                  const hasCleared = clearedMap.size > 0;
                  return (
                    <>
                      {hasActive && (
                        <button
                          className='btn-outline'
                          onClick={() => handleClearFindings()}
                          disabled={isClearingFindings}
                          style={{
                            borderColor: '#f04438',
                            color: '#f04438',
                            opacity: isClearingFindings ? 0.5 : 1,
                          }}
                        >
                          {isClearingFindings
                            ? 'Working…'
                            : 'Clear All Findings'}
                        </button>
                      )}
                      {hasCleared && (
                        <button
                          className='btn-outline'
                          onClick={() => handleRestoreFindings()}
                          disabled={isClearingFindings}
                          style={{
                            borderColor: '#10b981',
                            color: '#10b981',
                            opacity: isClearingFindings ? 0.5 : 1,
                          }}
                        >
                          {isClearingFindings
                            ? 'Working…'
                            : 'Restore All Findings'}
                        </button>
                      )}
                    </>
                  );
                })()}

                {!selectedLine.is_reviewed &&
                  selectedLine.parsedFlags?.length > 0 && (
                    <button
                      className='drawer-btn-outline'
                      onClick={handleMarkReviewed}
                      style={{ color: '#10b981', borderColor: '#10b981' }}
                    >
                      <CheckCircleOutlineIcon style={{ fontSize: 16 }} /> Mark
                      as Cleared
                    </button>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- Re-Run Modal Overlay (Re-Run vs Re-Run Excluding IEEPA) ---
          Replaces the old standalone "Exclude IEEPA" toggle next to the
          button: the choice now lives inside the Re-Run flow itself,
          matching the look of the Data Export / IEEPA download pickers
          (same audit-add-modal / bill-container-fit-screen-content-row
          classes). */}
      <Modal
        open={showReRunModal}
        onClose={() => !isReRunning && setShowReRunModal(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ outline: 'none' }}>
          <div className='audit-add-modal-cont'>
            <div className='audit-add-modal-cont-head'>
              <div className='audit-add-modal-head-cont-change'>
                <div className='audit-add-modal-head-cont-icon'>
                  <ReplayIcon
                    sx={{ color: '#4895EF', width: '25px', height: '24px' }}
                  />
                </div>
                <div className='audit-add-modal-head-cont-supporting'>
                  <h1>Re-Run Analysis</h1>
                  <p>Choose how this run should be re-analyzed.</p>
                </div>
              </div>
              <div
                className='audit-add-modal-head-close'
                onClick={() => setShowReRunModal(false)}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                >
                  <path
                    d='M18 6L6 18M6 6L18 18'
                    stroke='#85888E'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
            </div>
            <div className='activity-cont-frame-left-table-feed-row-cont-divider'></div>
            <div
              className='audit-add-modal-cont-body'
              style={{ paddingTop: '12px', paddingBottom: '12px' }}
            >
              <div className='audit-add-modal-cont-body-frame'>
                <div className='audit-add-modal-cont-body-frame-form'>
                  <div className='audit-add-modal-cont-body-frame-form-row'>
                    <div className='audit-add-modal-cont-body-frame-form-row-input'>
                      <div className='bill-container-fit-screen-content-row-selection'>
                        <div
                          className='bill-container-fit-screen-content-row-checkbox'
                          onClick={() => setExcludeIeepa(false)}
                        >
                          <div className='bill-container-fit-screen-content-row-input'>
                            <div
                              className={`bill-container-fit-screen-content-row-input-icon ${
                                !excludeIeepa ? 'selected' : ''
                              }`}
                            >
                              <div
                                className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                                  !excludeIeepa ? 'selected' : ''
                                }`}
                              />
                            </div>
                          </div>
                          <div className='bill-container-fit-screen-content-row-checkbox-text'>
                            <p>Re-Run</p>
                          </div>
                        </div>
                        <div
                          className='bill-container-fit-screen-content-row-checkbox'
                          onClick={() => setExcludeIeepa(true)}
                        >
                          <div className='bill-container-fit-screen-content-row-input'>
                            <div
                              className={`bill-container-fit-screen-content-row-input-icon ${
                                excludeIeepa ? 'selected' : ''
                              }`}
                            >
                              <div
                                className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                                  excludeIeepa ? 'selected' : ''
                                }`}
                              />
                            </div>
                          </div>
                          <div className='bill-container-fit-screen-content-row-checkbox-text'>
                            <p>Re-Run Excluding IEEPA</p>
                            <p
                              style={{
                                color: 'var(--text-secondary)',
                                fontSize: 12,
                                fontWeight: 400,
                                marginTop: 2,
                              }}
                            >
                              Use for entries already submitted to CAPE, where
                              IEEPA codes have been removed from the ACE
                              report.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='activity-cont-frame-left-table-feed-row-cont-divider'></div>
            <div className='audit-modal-cont-footer'>
              <div className='audit-modal-cont-footer-content'>
                <div
                  className='audit-modal-cont-footer-content-btn-cancel'
                  onClick={() => setShowReRunModal(false)}
                >
                  <div className='audit-modal-cont-footer-content-btn-text'>
                    Cancel
                  </div>
                </div>
                <div
                  className='audit-modal-cont-footer-content-btn-upload'
                  onClick={() => {
                    setShowReRunModal(false);
                    handleReRun(excludeIeepa);
                  }}
                >
                  <div
                    className='audit-modal-cont-footer-content-btn-text'
                    style={{ color: '#fff' }}
                  >
                    {isReRunning && (
                      <CircularProgress
                        style={{
                          width: '20px',
                          height: '20px',
                          marginRight: '10px',
                        }}
                        color='primary'
                      />
                    )}
                    Re-Run
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* --- Download Modal Overlay (S3 exports) --- */}
      <Modal
        open={showDownloadModal}
        onClose={() => !isDownloading && setShowDownloadModal(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ outline: 'none' }}>
          <DownloadModal
            handleClose={() => setShowDownloadModal(false)}
            setFileName={setDownloadFileName}
            downloadOpts={downloadOpts}
            setDownloadOpts={setDownloadOpts}
            handleCSV={handleExecuteDownload}
            triggerCSV={isDownloading}
            mode='s3'
            hasDiscrepancies={runDetails?.findings_count > 0}
            hasUnmatched={!!runDetails?.unmatched_s3_file_key}
          />
        </div>
      </Modal>

      {/* --- IEEPA Download Modal Overlay (CAPE Phase I vs All IEEPA Rows) ---
          Reuses the shared DownloadModal in a new 'ieepa' mode so the user
          gets the same look-and-feel as every other export picker in the
          app. handleCSV unwraps the { name } payload the modal emits on
          Download click and forwards the raw string into
          handleGenerateIeepaReport, which in turn passes the
          applyRules + customFileName bag to the backend. */}
      <Modal
        open={showIeepaModal}
        onClose={() => !isGeneratingIeepa && setShowIeepaModal(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ outline: 'none' }}>
          <DownloadModal
            handleClose={() => setShowIeepaModal(false)}
            setFileName={setIeepaFileName}
            downloadOpts={ieepaDownloadOpts}
            setDownloadOpts={setIeepaDownloadOpts}
            // DownloadModal invokes handleCSV({ name }) on click. Flatten
            // that to the raw name string so handleGenerateIeepaReport's
            // signature stays symmetric with handleExecuteDownload above.
            handleCSV={(payload: any) =>
              handleGenerateIeepaReport(payload?.name)
            }
            triggerCSV={isGeneratingIeepa}
            mode='ieepa'
            isOpen={showIeepaModal}
          />
        </div>
      </Modal>

      <DeleteConfirmationModal
        open={deleteModalOpen}
        handleClose={() => setDeleteModalOpen(false)}
        onDelete={executeDeleteRun}
        isDeleting={isDeleting}
        runName={runDetails?.name}
      />

      <Modal
        open={showFilterModal}
        onClose={() => !loading && setShowFilterModal(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            outline: 'none',
            width: '100%',
            maxWidth: '700px',
            padding: '16px',
          }}
        >
          <UnifiedFilterModal
            handleClose={() => setShowFilterModal(false)}
            activeTariffs={activeTariffFilters}
            activeFindings={activeFindingFilters}
            activeCountries={activeCountryFilters}
            availableTariffs={runFilterOptions.tariffPrefixes}
            availableFindings={runFilterOptions.flagTypes}
            availableCountries={runFilterOptions.countries}
            onApplyFilters={(tariffs, findings, countries) => {
              setActiveTariffFilters(tariffs);
              setActiveFindingFilters(findings);
              setActiveCountryFilters(countries);
              setCurrentPage(1);
            }}
            isLoading={loading}
          />
        </div>
      </Modal>

      <Modal
        open={showBulkActionsModal}
        onClose={() => !bulkActionLoading && setShowBulkActionsModal(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            outline: 'none',
            width: '100%',
            maxWidth: '1140px',
            padding: '16px',
          }}
        >
          <BulkActionsModal
            handleClose={() => setShowBulkActionsModal(false)}
            availableTariffs={runFilterOptions.tariffPrefixes}
            availableFindings={runFilterOptions.flagTypes}
            availableCountries={runFilterOptions.countries}
            onSubmit={handleBulkSubmit}
            onPreview={handleBulkPreview}
            scopeDescription={viewScopeLabel}
            isLoading={bulkActionLoading}
          />
        </div>
      </Modal>

      <AceTableExportModal
        open={showExportModal}
        handleClose={() => setShowExportModal(false)}
        onExport={handleExportTableCsv}
        isExporting={isExporting}
        totalRows={totalLines}
        rowLimit={CSV_EXPORT_ROW_LIMIT}
        activeTab={activeTab}
        hasActiveFilters={exportHasActiveFilters}
        defaultFileName={exportDefaultFileName}
      />
    </div>
  );
};

export default AceRunDetailsPage;
